/**
 * Vendor Guide — client logic
 *
 * Sections:
 *   - Config + constants
 *   - Auth helpers (rep session, JWT Bearer via window.RepPortal.API)
 *   - State (view mode, taxonomy, vendors, selections, filters)
 *   - API wrappers
 *   - Stats computation
 *   - Rendering: category view (tiles → subcats → ranked vendors)
 *   - Rendering: vendor view (A-Z grid, filtered)
 *   - Rendering: vendor card (shared) + placeholder logo + badges
 *   - Filter bar + search
 *   - Event wiring + init
 */
(function () {
  'use strict';

  var WORKER_URL = 'https://vendor-worker.ryan-29b.workers.dev';
  var LOGIN_PAGE = '/pages/rep-quote-portal';

  var TIER_ORDER = ['best', 'better', 'good'];
  var TIER_LABELS = { best: 'Best', better: 'Better', good: 'Good' };
  // Frontend admin list — mirrors the worker's ADMIN_REP_CODES secret (the
  // worker is the real gate, so 403s still land if this list drifts).
  var ADMIN_CODES = ['RW'];
  var MARGIN_LABELS = { 1: '$', 2: '$$', 3: '$$$', 4: '$$$$' };

  // ------------------------------------------------------------------
  // Auth
  // ------------------------------------------------------------------
  function getSession() {
    try {
      var raw = sessionStorage.getItem('rep_portal_session') ||
                localStorage.getItem('rep_portal_session');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function requireSession() {
    var s = getSession();
    if (!s || !s.code) {
      window.location.href = LOGIN_PAGE;
      return null;
    }
    return s;
  }

  async function apiFetch(path, options) {
    var opts = options || {};
    var session = getSession();
    if (!session || !session.code) {
      window.location.href = LOGIN_PAGE;
      throw new Error('Not authenticated');
    }
    // Route through the shared RepPortal.API helper — handles JWT refresh + 401 retry.
    if (window.RepPortal && window.RepPortal.API && window.RepPortal.API.fetchExternal) {
      return window.RepPortal.API.fetchExternal(WORKER_URL, path, opts);
    }
    // Fallback for pages that somehow load before custom-rep-portal.js — raw Bearer fetch.
    if (!session.accessToken) {
      window.location.href = LOGIN_PAGE;
      throw new Error('Not authenticated');
    }
    var headers = Object.assign(
      { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.accessToken },
      opts.headers || {}
    );
    var resp = await fetch(WORKER_URL + path, Object.assign({}, opts, { headers: headers }));
    var data = null;
    try { data = await resp.json(); } catch (e) { data = null; }
    if (!resp.ok) {
      var msg = (data && data.error) || ('Request failed (' + resp.status + ')');
      var err = new Error(msg);
      err.status = resp.status;
      throw err;
    }
    return data;
  }

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------
  var state = {
    view: 'vendor',              // 'vendor' | 'category'
    taxonomy: null,              // { categories: [...] }
    vendors: [],                 // [{...vendor}]
    selectedCategoryKey: null,
    selectedSubcategoryKey: null,
    groupBy: 'az',               // 'az' | 'tier' | 'margin'
    filters: {
      search: '',
      category: [],              // category keys
      tier: [],                  // 'best' | 'better' | 'good'
      margin: [],                // '1' | '2' | '3' | '4'
      flags: []                  // 'custom' | 'inStock' | 'confidential'
    },
    session: null,
    adminMode: false,
    modal: {
      open: false,
      mode: null,                // 'view' | 'edit' | 'new'
      vendorId: null,
      draft: null,
      activeTab: 'info'          // 'info' | 'po' — admin edit modal tab
    },
    confirm: { open: false, onConfirm: null },
    recipients: null,            // { [repCode]: { name, email, optin } } | null
    /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-09-19) — one vendor's product list for the
       pallet-fee exclusion picker, keyed by vendor name. Fetched once per vendor per page load;
       the modal re-renders on every keystroke elsewhere in the form, and re-fetching a 120-product
       list each time would make the picker feel broken. */
    pfxCache: {},                // { [vendorName]: { loading? , products?, building?, error? } }
    recipientsLoading: false
  };

  var els = {};

  // ------------------------------------------------------------------
  // Utilities
  // ------------------------------------------------------------------
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function initial(name) {
    var s = String(name || '').trim();
    return s ? s.charAt(0).toUpperCase() : '?';
  }

  // Deterministic color for placeholder logos — hash the vendor name.
  function colorFromName(name) {
    var s = String(name || '');
    var h = 0;
    for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    var hue = Math.abs(h) % 360;
    return 'hsl(' + hue + ', 55%, 48%)';
  }

  function marginString(n) {
    var v = Math.max(1, Math.min(4, Number(n) || 1));
    return '$'.repeat(v);
  }

  function marginRangeHtml(margins) {
    if (!margins || !margins.length) return '';
    var sorted = margins.slice().sort(function (a, b) { return a - b; });
    var lo = sorted[0], hi = sorted[sorted.length - 1];
    var label = (lo === hi) ? marginString(lo) : marginString(lo) + '-' + marginString(hi);
    return '<span class="vg-badge vg-badge--margin vg-badge--margin-' + hi + '" title="Margin">' + label + '</span>';
  }

  function categoryByKey(key) {
    if (!state.taxonomy) return null;
    for (var i = 0; i < state.taxonomy.categories.length; i++) {
      if (state.taxonomy.categories[i].key === key) return state.taxonomy.categories[i];
    }
    return null;
  }

  function subcategoryByKey(subKey) {
    if (!state.taxonomy) return null;
    var cats = state.taxonomy.categories;
    for (var i = 0; i < cats.length; i++) {
      var subs = cats[i].subcategories || [];
      for (var j = 0; j < subs.length; j++) {
        if (subs[j].key === subKey) return { category: cats[i], subcategory: subs[j] };
      }
    }
    return null;
  }

  function vendorsForSubcategory(subKey) {
    var out = [];
    state.vendors.forEach(function (v) {
      var placements = v.placements || [];
      for (var i = 0; i < placements.length; i++) {
        if (placements[i].subcategoryKey === subKey) {
          out.push({ vendor: v, placement: placements[i] });
          break;
        }
      }
    });
    return out;
  }

  function bestTierForVendor(vendor) {
    var placements = vendor.placements || [];
    var rank = { best: 3, better: 2, good: 1 };
    var best = null;
    for (var i = 0; i < placements.length; i++) {
      var tiers = placements[i].tiers || (placements[i].tier ? [String(placements[i].tier).toLowerCase()] : []);
      for (var j = 0; j < tiers.length; j++) {
        if (rank[tiers[j]] && (!best || rank[tiers[j]] > rank[best])) best = tiers[j];
      }
    }
    return best;
  }

  function allMarginsForVendor(vendor) {
    var placements = vendor.placements || [];
    var seen = {};
    var result = [];
    for (var i = 0; i < placements.length; i++) {
      var ms = placements[i].margins || (placements[i].margin ? [Number(placements[i].margin)] : []);
      for (var j = 0; j < ms.length; j++) {
        var m = Number(ms[j]);
        if (m && !seen[m]) { seen[m] = true; result.push(m); }
      }
    }
    return result.sort(function (a, b) { return a - b; });
  }

  function allTiersForVendor(vendor) {
    var placements = vendor.placements || [];
    var seen = {};
    var result = [];
    for (var i = 0; i < placements.length; i++) {
      var ts = placements[i].tiers || (placements[i].tier ? [String(placements[i].tier).toLowerCase()] : []);
      for (var j = 0; j < ts.length; j++) {
        if (!seen[ts[j]]) { seen[ts[j]] = true; result.push(ts[j]); }
      }
    }
    return result;
  }

  function isAdmin() {
    return state.session && ADMIN_CODES.indexOf(state.session.code) !== -1;
  }

  function vendorById(id) {
    for (var i = 0; i < state.vendors.length; i++) {
      if (state.vendors[i].id === id) return state.vendors[i];
    }
    return null;
  }

  function upsertVendor(vendor) {
    var idx = -1;
    for (var i = 0; i < state.vendors.length; i++) {
      if (state.vendors[i].id === vendor.id) { idx = i; break; }
    }
    if (idx === -1) state.vendors.push(vendor);
    else state.vendors[idx] = vendor;
  }

  function removeVendorById(id) {
    state.vendors = state.vendors.filter(function (v) { return v.id !== id; });
  }

  // ------------------------------------------------------------------
  // Stats
  // ------------------------------------------------------------------
  // ------------------------------------------------------------------
  // Rendering — logo (shared)
  // ------------------------------------------------------------------
  function renderLogo(vendor) {
    if (vendor.logoUrl) {
      return '<span class="vg-logo"><img src="' + escapeHtml(vendor.logoUrl) +
             '" alt="' + escapeHtml(vendor.name) + ' logo" loading="lazy"></span>';
    }
    var color = colorFromName(vendor.name);
    return '<span class="vg-logo vg-logo--placeholder" style="background:' + color + ';">' +
           escapeHtml(initial(vendor.name)) + '</span>';
  }

  // ------------------------------------------------------------------
  // Rendering — Category View
  // ------------------------------------------------------------------
  var CATEGORY_LEVEL_DEPTH = { grid: 0, subcat: 1, ranked: 2 };
  function showCategoryLevel(level) {
    // level: 'grid' | 'subcat' | 'ranked'
    var pairs = [['grid', els.catGrid], ['subcat', els.subcat], ['ranked', els.ranked]];
    var prevDepth = CATEGORY_LEVEL_DEPTH[state._lastCatLevel || 'grid'];
    var nextDepth = CATEGORY_LEVEL_DEPTH[level];
    var direction = nextDepth >= prevDepth ? 'forward' : 'back';

    pairs.forEach(function (p) {
      var name = p[0];
      var el = p[1];
      var shouldShow = (name === level);
      if (shouldShow) {
        var wasHidden = el.hidden;
        el.hidden = false;
        if (wasHidden) {
          el.classList.remove('vg-slide--forward', 'vg-slide--back');
          void el.offsetWidth; // force reflow so animation re-runs
          el.classList.add('vg-slide--' + direction);
        }
      } else {
        el.hidden = true;
      }
    });
    state._lastCatLevel = level;
  }

  function countVendorsInCategory(catKey) {
    var cat = categoryByKey(catKey);
    if (!cat) return 0;
    var subs = cat.subcategories || [];
    var ids = {};
    subs.forEach(function (sub) {
      vendorsForSubcategory(sub.key).forEach(function (e) { ids[e.vendor.id] = true; });
    });
    return Object.keys(ids).length;
  }

  function renderCategoryTiles() {
    if (!state.taxonomy) return;
    var html = state.taxonomy.categories.map(function (cat) {
      var subCount = (cat.subcategories || []).length;
      var vendorCount = countVendorsInCategory(cat.key);
      return (
        '<button type="button" class="vg-cat-tile" data-cat-key="' + escapeHtml(cat.key) + '">' +
          '<span class="vg-cat-tile__name">' + escapeHtml(cat.name) + '</span>' +
          '<span class="vg-cat-tile__meta">' + subCount + ' sub-categories</span>' +
          '<span class="vg-cat-tile__count">' + vendorCount + '</span>' +
        '</button>'
      );
    }).join('');
    els.catGrid.innerHTML = html;
  }

  function renderSubcategoryList(categoryKey) {
    var cat = categoryByKey(categoryKey);
    if (!cat) return;
    els.subcatTitle.textContent = cat.name;

    var subs = cat.subcategories || [];
    var html = subs.map(function (sub) {
      var count = vendorsForSubcategory(sub.key).length;
      return (
        '<button type="button" class="vg-subcat__item" data-sub-key="' + escapeHtml(sub.key) + '">' +
          '<span class="vg-subcat__name">' + escapeHtml(sub.name) + '</span>' +
          '<span class="vg-subcat__count">' + count + ' vendor' + (count === 1 ? '' : 's') + '</span>' +
        '</button>'
      );
    }).join('');
    els.subcatList.innerHTML = html || '<p class="vg-empty"><span class="vg-empty__text">No sub-categories.</span></p>';
  }

  function renderRankedVendors(subKey) {
    var info = subcategoryByKey(subKey);
    if (!info) return;
    els.rankedTitle.textContent = info.subcategory.name;
    els.backToSubcat.textContent = info.category.name;
    els.backToSubcat.setAttribute('data-cat-key', info.category.key);

    var entries = vendorsForSubcategory(subKey);
    if (!entries.length) {
      els.tiers.innerHTML = emptyBlock('No vendors placed in this sub-category yet.');
      return;
    }

    // Bucket by highest tier
    var tierRank = { best: 3, better: 2, good: 1 };
    var buckets = { best: [], better: [], good: [] };
    entries.forEach(function (e) {
      var pTiers = e.placement.tiers || (e.placement.tier ? [String(e.placement.tier).toLowerCase()] : []);
      var highest = 'good';
      pTiers.forEach(function (t) { if ((tierRank[t] || 0) > (tierRank[highest] || 0)) highest = t; });
      if (buckets[highest]) buckets[highest].push(e);
    });

    // Sort each tier: best margin (smaller $) first, then name
    Object.keys(buckets).forEach(function (t) {
      buckets[t].sort(function (a, b) {
        var aMargins = a.placement.margins || (a.placement.margin ? [Number(a.placement.margin)] : [1]);
        var bMargins = b.placement.margins || (b.placement.margin ? [Number(b.placement.margin)] : [1]);
        var ma = Math.min.apply(null, aMargins);
        var mb = Math.min.apply(null, bMargins);
        if (ma !== mb) return ma - mb;
        return String(a.vendor.name).localeCompare(String(b.vendor.name));
      });
    });

    var html = TIER_ORDER.map(function (tier) {
      var list = buckets[tier];
      if (!list.length) return '';
      var cards = list.map(function (e) { return renderVendorCard(e.vendor, e.placement); }).join('');
      return (
        '<div class="vg-tier vg-tier--' + tier + '">' +
          '<div class="vg-tier__header">' +
            '<span class="vg-tier__badge vg-tier__badge--' + tier + '">' + TIER_LABELS[tier] + '</span>' +
            '<span class="vg-tier__count">' + list.length + ' vendor' + (list.length === 1 ? '' : 's') + '</span>' +
          '</div>' +
          '<div class="vg-tier__grid">' + cards + '</div>' +
        '</div>'
      );
    }).join('');

    els.tiers.innerHTML = html || emptyBlock('No vendors placed in this sub-category yet.');
    tagOverflowingNames(els.tiers);
  }

  function emptyBlock(msg) {
    return (
      '<div class="vg-empty">' +
        '<div class="vg-empty__title">Nothing here yet</div>' +
        '<div class="vg-empty__text">' + escapeHtml(msg) + '</div>' +
      '</div>'
    );
  }

  function tagOverflowingNames(container) {
    var names = (container || document).querySelectorAll('.vg-card__name');
    for (var i = 0; i < names.length; i++) {
      var el = names[i];
      if (el.scrollWidth > el.clientWidth) {
        el.classList.add('vg-card__name--overflows');
        el.style.setProperty('--vg-name-overflow', '-' + (el.scrollWidth - el.clientWidth) + 'px');
      } else {
        el.classList.remove('vg-card__name--overflows');
      }
    }
  }

  function renderVendorCard(vendor, placement) {
    var tierClass = '';
    var tierBadges = [];
    var marginHtml = '';
    var leadHtml = '';
    var availHtml = '';
    var confHtml = '';

    var _tierRank = { best: 3, better: 2, good: 1 };
    function _highestTier(arr) {
      var h = 'good';
      arr.forEach(function (t) { if ((_tierRank[t] || 0) > (_tierRank[h] || 0)) h = t; });
      return h;
    }
    if (placement) {
      var pTiers = placement.tiers || (placement.tier ? [String(placement.tier).toLowerCase()] : []);
      if (pTiers.length) tierClass = ' vg-card--' + _highestTier(pTiers);
      ['good', 'better', 'best'].forEach(function (t) {
        if (pTiers.indexOf(t) !== -1) tierBadges.push('<span class="vg-badge vg-badge--tier-' + t + '">' + TIER_LABELS[t] + '</span>');
      });
    } else {
      var vTiers = allTiersForVendor(vendor);
      if (vTiers.length) tierClass = ' vg-card--' + _highestTier(vTiers);
      ['good', 'better', 'best'].forEach(function (t) {
        if (vTiers.indexOf(t) !== -1) tierBadges.push('<span class="vg-badge vg-badge--tier-' + t + '">' + TIER_LABELS[t] + '</span>');
      });
    }

    var marginLabel = '';
    if (placement) {
      var pMargins = placement.margins || (placement.margin ? [Number(placement.margin)] : []);
      if (pMargins.length) {
        var ms = pMargins.slice().sort(function (a, b) { return a - b; });
        marginLabel = (ms[0] === ms[ms.length - 1]) ? marginString(ms[0]) : marginString(ms[0]) + '-' + marginString(ms[ms.length - 1]);
      }
    } else {
      var vMargins = allMarginsForVendor(vendor);
      if (vMargins.length) {
        marginLabel = (vMargins[0] === vMargins[vMargins.length - 1]) ? marginString(vMargins[0]) : marginString(vMargins[0]) + '-' + marginString(vMargins[vMargins.length - 1]);
      }
    }
    if (vendor.leadTime) {
      leadHtml = '<span class="vg-badge vg-badge--lead">' + escapeHtml(vendor.leadTime) + '</span>';
    }
    if (vendor.availability === 'custom') {
      availHtml = '<span class="vg-badge vg-badge--custom">Custom</span>';
    } else if (vendor.availability === 'in-stock') {
      availHtml = '<span class="vg-badge vg-badge--instock">In-stock</span>';
    }
    var rows = [];
    if (vendor.defunct) {
      rows.push('<div class="vg-card__row"><span class="vg-badge vg-badge--defunct">OUT OF BUSINESS</span></div>');
    } else {
      if (tierBadges.length) rows.push('<div class="vg-card__row">' + tierBadges.join('') + '</div>');
      if (leadHtml) rows.push('<div class="vg-card__row">' + leadHtml + '</div>');
      if (availHtml) rows.push('<div class="vg-card__row">' + availHtml + '</div>');
    }

    var marginCorner = '';
    if (vendor.confidential) {
      marginCorner = '<span class="vg-card__margin vg-card__margin--conf">REQUEST PRICING</span>';
    } else if (marginLabel) {
      marginCorner = '<span class="vg-card__margin">' + marginLabel + '</span>';
    }

    var defunctClass = vendor.defunct ? ' vg-card--defunct' : '';

    var editBtn = isAdmin()
      ? '<button type="button" class="vg-card__edit-btn" data-vg-edit-vendor="' + escapeHtml(vendor.id) + '" aria-label="Edit ' + escapeHtml(vendor.name) + '" title="Edit">&#9998;</button>'
      : '';

    return (
      '<div class="vg-card' + tierClass + defunctClass + '" data-vendor-id="' + escapeHtml(vendor.id) + '" role="button" tabindex="0">' +
        editBtn +
        '<div class="vg-card__title-row">' +
          '<div class="vg-card__name-clip"><h3 class="vg-card__name">' + escapeHtml(vendor.name) + '</h3></div>' +
          marginCorner +
        '</div>' +
        '<div class="vg-card__head">' +
          renderLogo(vendor) +
          (rows.length ? '<div class="vg-card__meta">' + rows.join('') + '</div>' : '') +
        '</div>' +
      '</div>'
    );
  }

  // ------------------------------------------------------------------
  // Rendering — Vendor View (A-Z grid, filtered)
  // ------------------------------------------------------------------
  function vendorMatchesFilters(vendor) {
    var f = state.filters;

    // Search
    if (f.search) {
      if (String(vendor.name || '').toLowerCase().indexOf(f.search) === -1) return false;
    }

    var placements = vendor.placements || [];

    // Category filter — any placement in a selected category
    if (f.category.length) {
      var catKeys = {};
      f.category.forEach(function (k) {
        var cat = categoryByKey(k);
        if (cat) (cat.subcategories || []).forEach(function (s) { catKeys[s.key] = true; });
      });
      var anyCat = placements.some(function (p) { return catKeys[p.subcategoryKey]; });
      if (!anyCat) return false;
    }

    // Tier filter — any placement matches a selected tier
    if (f.tier.length) {
      var anyTier = placements.some(function (p) {
        var pTiers = p.tiers || (p.tier ? [String(p.tier).toLowerCase()] : []);
        return pTiers.some(function (t) { return f.tier.indexOf(t) !== -1; });
      });
      if (!anyTier) return false;
    }

    // Margin filter — any placement matches a selected margin
    if (f.margin.length) {
      var anyMargin = placements.some(function (p) {
        var pMargins = p.margins || (p.margin ? [String(p.margin)] : []);
        return pMargins.some(function (m) { return f.margin.indexOf(String(m)) !== -1; });
      });
      if (!anyMargin) return false;
    }

    // Flags filter
    if (f.flags.length) {
      if (f.flags.indexOf('custom') !== -1 && vendor.availability !== 'custom') return false;
      if (f.flags.indexOf('inStock') !== -1 && vendor.availability !== 'in-stock') return false;
      if (f.flags.indexOf('confidential') !== -1 && !vendor.confidential) return false;
      if (f.flags.indexOf('defunct') !== -1 && !vendor.defunct) return false;
    }

    return true;
  }

  function renderVendorView() {
    var filtered = state.vendors.filter(vendorMatchesFilters);

    if (!state.vendors.length) {
      els.vendorGrid.innerHTML = emptyBlock('No vendors yet.');
      els.az.innerHTML = '';
      return;
    }
    if (!filtered.length) {
      els.vendorGrid.innerHTML = emptyBlock('No vendors match the current filters.');
      els.az.innerHTML = '';
      return;
    }

    var sorted = filtered.slice().sort(function (a, b) {
      return String(a.name).localeCompare(String(b.name));
    });

    var mode = state.groupBy || 'az';

    if (mode === 'tier') {
      renderVendorViewByTier(sorted);
    } else if (mode === 'margin') {
      renderVendorViewByMargin(sorted);
    } else {
      renderVendorViewAZ(sorted);
    }
    tagOverflowingNames(els.vendorGrid);
    updateGroupByButtons();
  }

  function renderVendorViewAZ(sorted) {
    els.az.hidden = false;
    var groups = {};
    sorted.forEach(function (v) {
      var letter = (String(v.name).charAt(0) || '#').toUpperCase();
      if (!/[A-Z]/.test(letter)) letter = '#';
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(v);
    });

    var letters = Object.keys(groups).sort();

    var alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    var azHtml = alphabet.map(function (l) {
      var active = groups[l];
      return (
        '<a href="#vg-letter-' + l + '" class="vg-az__letter' +
        (active ? '' : ' vg-az__letter--inactive') + '"' +
        (active ? '' : ' aria-disabled="true" tabindex="-1"') + '>' + l + '</a>'
      );
    }).join('');
    if (groups['#']) azHtml += '<a href="#vg-letter-num" class="vg-az__letter">#</a>';
    els.az.innerHTML = azHtml;

    var html = letters.map(function (letter) {
      var anchorId = letter === '#' ? 'vg-letter-num' : 'vg-letter-' + letter;
      var cards = groups[letter].map(function (v) { return renderVendorCard(v); }).join('');
      return (
        '<div class="vg-vendor-group" id="' + anchorId + '">' +
          '<h2 class="vg-vendor-group__letter">' + letter + '</h2>' +
          '<div class="vg-vendor-group__grid">' + cards + '</div>' +
        '</div>'
      );
    }).join('');
    els.vendorGrid.innerHTML = html;
  }

  function renderVendorViewByTier(sorted) {
    els.az.innerHTML = '';
    els.az.hidden = true;
    var TIER_ORDER = ['best', 'better', 'good'];
    var buckets = { best: [], better: [], good: [], unrated: [] };
    sorted.forEach(function (v) {
      var highest = bestTierForVendor(v);
      if (highest) buckets[highest].push(v);
      else buckets.unrated.push(v);
    });

    var html = TIER_ORDER.map(function (tier) {
      if (!buckets[tier].length) return '';
      var cards = buckets[tier].map(function (v) { return renderVendorCard(v); }).join('');
      return (
        '<div class="vg-vendor-group">' +
          '<h2 class="vg-vendor-group__letter"><span class="vg-group-heading vg-group-heading--' + tier + '">' + TIER_LABELS[tier] + '</span> <span class="vg-vendor-group__count">(' + buckets[tier].length + 'x)</span></h2>' +
          '<div class="vg-vendor-group__grid">' + cards + '</div>' +
        '</div>'
      );
    }).join('');
    if (buckets.unrated.length) {
      var uCards = buckets.unrated.map(function (v) { return renderVendorCard(v); }).join('');
      html += '<div class="vg-vendor-group"><h2 class="vg-vendor-group__letter">Unrated <span class="vg-vendor-group__count">(' + buckets.unrated.length + 'x)</span></h2><div class="vg-vendor-group__grid">' + uCards + '</div></div>';
    }
    els.vendorGrid.innerHTML = html || emptyBlock('No vendors.');
  }

  function renderVendorViewByMargin(sorted) {
    els.az.innerHTML = '';
    els.az.hidden = true;
    var MARGIN_ORDER = [4, 3, 2, 1];
    var MARGIN_LABELS = { 4: '$$$$', 3: '$$$', 2: '$$', 1: '$' };
    var buckets = { 4: [], 3: [], 2: [], 1: [], unrated: [] };
    sorted.forEach(function (v) {
      var ms = allMarginsForVendor(v);
      if (ms.length) {
        var hi = ms[ms.length - 1];
        buckets[hi].push(v);
      } else {
        buckets.unrated.push(v);
      }
    });

    var html = MARGIN_ORDER.map(function (m) {
      if (!buckets[m].length) return '';
      var cards = buckets[m].map(function (v) { return renderVendorCard(v); }).join('');
      return (
        '<div class="vg-vendor-group">' +
          '<h2 class="vg-vendor-group__letter"><span class="vg-group-heading vg-group-heading--margin-' + m + '">' + MARGIN_LABELS[m] + '</span> <span class="vg-vendor-group__count">(' + buckets[m].length + 'x)</span></h2>' +
          '<div class="vg-vendor-group__grid">' + cards + '</div>' +
        '</div>'
      );
    }).join('');
    if (buckets.unrated.length) {
      var uCards = buckets.unrated.map(function (v) { return renderVendorCard(v); }).join('');
      html += '<div class="vg-vendor-group"><h2 class="vg-vendor-group__letter">Unrated <span class="vg-vendor-group__count">(' + buckets.unrated.length + 'x)</span></h2><div class="vg-vendor-group__grid">' + uCards + '</div></div>';
    }
    els.vendorGrid.innerHTML = html || emptyBlock('No vendors.');
  }

  function updateGroupByButtons() {
    var btns = document.querySelectorAll('[data-vg-group-by]');
    for (var i = 0; i < btns.length; i++) {
      var active = btns[i].getAttribute('data-vg-group-by') === state.groupBy;
      btns[i].classList.toggle('vg-group-by__btn--active', active);
    }
  }

  // ------------------------------------------------------------------
  // Filter bar
  // ------------------------------------------------------------------
  function populateCategoryFilterOptions() {
    var dd = qs('[data-vg-filter-dropdown="category"]');
    if (!dd || !state.taxonomy) return;
    var html = state.taxonomy.categories.map(function (cat) {
      return (
        '<label class="vg-filter__item">' +
          '<input type="checkbox" data-vg-filter="category" value="' + escapeHtml(cat.key) + '"> ' +
          escapeHtml(cat.name) +
        '</label>'
      );
    }).join('');
    dd.innerHTML = html;
  }

  function syncFilterButtonState() {
    ['category', 'tier', 'margin', 'flags'].forEach(function (key) {
      var count = state.filters[key].length;
      var btn = qs('[data-vg-filter-toggle="' + key + '"]');
      var countEl = qs('[data-vg-filter-count="' + key + '"]');
      if (btn) btn.classList.toggle('vg-filter__btn--active', count > 0);
      if (countEl) {
        if (count > 0) {
          countEl.textContent = count;
          countEl.hidden = false;
        } else {
          countEl.hidden = true;
        }
      }
    });
  }

  function collectFilterValues(key) {
    return qsa('[data-vg-filter="' + key + '"]:checked').map(function (el) { return el.value; });
  }

  function applyFilters() {
    state.filters.search = (els.search.value || '').trim().toLowerCase();
    state.filters.category = collectFilterValues('category');
    state.filters.tier = collectFilterValues('tier');
    state.filters.margin = collectFilterValues('margin');
    state.filters.flags = collectFilterValues('flags');
    syncFilterButtonState();
    if (state.view === 'vendor') renderVendorView();
  }

  function clearFilters() {
    qsa('[data-vg-filter]').forEach(function (cb) { cb.checked = false; });
    if (els.search) els.search.value = '';
    applyFilters();
  }

  function toggleFilterDropdown(key) {
    var dd = qs('[data-vg-filter-dropdown="' + key + '"]');
    if (!dd) return;
    // Close others
    qsa('.vg-filter__dropdown').forEach(function (other) {
      if (other !== dd) other.classList.remove('vg-filter__dropdown--open');
    });
    dd.classList.toggle('vg-filter__dropdown--open');
  }

  function closeAllDropdowns() {
    qsa('.vg-filter__dropdown').forEach(function (dd) {
      dd.classList.remove('vg-filter__dropdown--open');
    });
  }

  // ------------------------------------------------------------------
  // View switching
  // ------------------------------------------------------------------
  function setView(view) {
    state.view = view;
    qsa('[data-view]', els.viewToggle).forEach(function (btn) {
      var active = btn.getAttribute('data-view') === view;
      btn.classList.toggle('vg-view-toggle__btn--active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    qsa('[data-vg-view]').forEach(function (el) {
      el.hidden = el.getAttribute('data-vg-view') !== view;
    });

    // Filters bar only visible in vendor view
    if (els.filters) els.filters.hidden = (view !== 'vendor');

    if (view === 'category') {
      state.selectedCategoryKey = null;
      state.selectedSubcategoryKey = null;
      renderCategoryTiles();
      showCategoryLevel('grid');
    } else if (view === 'vendor') {
      renderVendorView();
    } else if (view === 'po-profiles') {
      renderPoProfilesView();
    }
  }

  // Push the current vendor's PO data onto one or more other vendors (parent-
  // company groupings, e.g. Golden Designs → Maxxus / Dynamic / Dynamic Cold
  // Therapy). Reads the current form state as the source-of-truth so unsaved
  // edits are included, then PUTs each target via vendor-worker.
  // ── Vendor PO Profile: Default Instructions list editors (one per shipping mode) ──
  // Each editor element carries data-mode="vendor"|"bsfs" and writes its serialized
  // newline-joined value back to a sibling hidden input keyed by mode.
  function poInstrHiddenFieldFor(mode) {
    return mode === 'bsfs' ? 'defaultInstructionsBsfs' : 'defaultInstructionsVendor';
  }
  function syncVendorPoInstrHidden(editor) {
    if (!editor) return;
    var mode = editor.getAttribute('data-mode') || 'vendor';
    var list = editor.querySelector('.vg-po-instr-list');
    var fieldName = poInstrHiddenFieldFor(mode);
    var hidden = els.modalBody && els.modalBody.querySelector('[data-vg-po-field="' + fieldName + '"]');
    if (!list || !hidden) return;
    // CUSTOM_VGUX_BY_CLAUDE (#11): a line shown with its bold / italic carries the stored text (marks and all) on
    // data-raw (custom-vendor-guide-po-form.js); its textContent has lost the asterisks.
    var lines = qsa('.vg-po-instr-text', list).map(function (s) {
      var raw = s.getAttribute('data-raw');
      return raw != null ? raw : (s.textContent || '');
    }).filter(Boolean);
    hidden.value = lines.join('\n');
  }
  function syncAllVendorPoInstrHidden() {
    if (!els.modalBody) return;
    qsa('[data-vg-po-instr-editor]', els.modalBody).forEach(syncVendorPoInstrHidden);
  }
  function addVendorPoInstructionTo(editor) {
    if (!editor) return;
    var input = editor.querySelector('[data-vg-po-instr-input]');
    var list = editor.querySelector('.vg-po-instr-list');
    if (!input || !list) return;
    var v = (input.value || '').trim();
    if (!v) return;
    var li = document.createElement('li');
    li.className = 'vg-po-instr-item';
    li.innerHTML = '<span class="vg-po-instr-text">' + escapeHtml(v) + '</span>'
      + '<button type="button" class="vg-po-instr-x" data-vg-po-instr-remove aria-label="Remove">&times;</button>';
    list.appendChild(li);
    input.value = '';
    syncVendorPoInstrHidden(editor);
    input.focus();
  }

  async function copyPoToOtherVendors(applyBtn) {
    var draft = collectDraftFromForm();
    if (!poIsFilled(draft.po)) {
      showToast('Fill in PO data above before copying.', 'error');
      return;
    }
    var targets = qsa('[data-vg-po-copy-target]:checked', els.modalBody)
      .map(function (cb) { return cb.getAttribute('data-vg-po-copy-target'); });
    if (targets.length === 0) {
      showToast('Pick at least one vendor to copy to.', 'error');
      return;
    }
    var targetVendors = targets
      .map(function (id) { return (state.vendors || []).find(function (v) { return v && v.id === id; }); })
      .filter(Boolean);
    var names = targetVendors.map(function (v) { return v.name; }).join(', ');
    if (!confirm('Copy this PO data to ' + targetVendors.length + ' vendor' + (targetVendors.length > 1 ? 's' : '') + ' (' + names + ')? Existing PO data on those vendors will be overwritten.')) return;

    applyBtn.disabled = true;
    var orig = applyBtn.textContent;
    applyBtn.textContent = 'Copying…';
    var ok = 0; var failed = [];
    for (var i = 0; i < targetVendors.length; i++) {
      var target = targetVendors[i];
      try {
        var _poCopy = JSON.parse(JSON.stringify(draft.po));
        /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — never copy Quote Alias to a sibling.
           An alias is a CLAIM on a quote name, and the resolver deliberately refuses to guess
           between two vendors claiming the same one. Copying PO data to three siblings would
           manufacture four claimants out of nothing and turn every white-label row ambiguous.
           Deleting the key (rather than sending []) leaves whatever the target already had —
           the worker keeps a field it isn't sent. */
        delete _poCopy.quoteAliases;
        /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-27) - and never copy the Alias Keywords
           either, for a sharper version of the same reason: they are globally unique, so copying
           them to a sibling does not merely muddy a claim, it makes the target's very next save
           fail the uniqueness check against the vendor it was copied FROM. */
        delete _poCopy.aliasKeywords;
        /* END CUSTOM_VENDOR_GUIDE_BY_CLAUDE */
        /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Ryan 2026-10-04) - every save names the revision it was based
           on, so a change made in Product Rules since this page loaded refuses the copy instead of being
           silently undone. */
        var updated = Object.assign({}, target, { po: _poCopy, basedOn: String(target.updatedAt || 0) });
        var res = await apiFetch('/vendor/' + encodeURIComponent(target.id), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updated),
        });
        // apiFetch throws on non-2xx; if we got here it worked. Update local cache.
        if (res && res.vendor) {
          var idx = state.vendors.findIndex(function (v) { return v && v.id === target.id; });
          if (idx >= 0) state.vendors[idx] = res.vendor;
          ok++;
        } else { ok++; }
      } catch (err) {
        failed.push(target.name + ': ' + (err && err.message ? err.message : 'failed'));
      }
    }
    applyBtn.disabled = false;
    applyBtn.textContent = orig;
    if (failed.length) {
      alert('Copied to ' + ok + ' of ' + targetVendors.length + '.\n\nFailures:\n' + failed.join('\n'));
    } else {
      showToast('Copied PO data to ' + ok + ' vendor' + (ok > 1 ? 's' : '') + '.', 'success');
    }
    // Collapse the panel + uncheck targets
    var panel = els.modalBody.querySelector('[data-vg-po-copy-panel]');
    var toggle = els.modalBody.querySelector('[data-vg-po-copy-toggle]');
    if (panel) panel.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    qsa('[data-vg-po-copy-target]', els.modalBody).forEach(function (cb) { cb.checked = false; });
  }

  var RFQ_WORKER_URL = 'https://rfq-worker.ryan-29b.workers.dev';

  /* CUSTOM_GUIDEWRITE_BY_CLAUDE (AIOPS `78278a7aa193`, Ryan 2026-09-12) — WHAT AN AGENT CHANGED.
     ─────────────────────────────────────────────────────────────────────────
     An AI agent can now fill this Guide from an interview with Ryan. The agent path and the admin
     path both exist; this is the third surface — a person opening the portal can SEE what moved,
     which is what makes a silent failure (or a wrong edit) noticeable at all.

     ⛔ IT SHOWS BEFORE AND AFTER, NOT A LIST OF EVENTS. rfq-worker's /audit-logs returns index
     rows and drops `detail`, so an audit-backed panel would say "somebody changed ANCORE" and
     nothing else. GET /vendor-guide/write-log returns the records.

     ⚠️ IT DOES NOT LIST EDITS MADE ON THIS SCREEN. Those go straight to vendor-worker and were
     never logged here; claiming otherwise would be worse than the gap. The panel says so. */
  function fmtWriteLogTime(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso || '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  function renderWriteLogEntry(e) {
    var fields = Array.isArray(e.fields) ? e.fields : [];
    var rows = fields.map(function (f) {
      var before = String(f.before == null ? '' : f.before);
      var after = String(f.after == null ? '' : f.after);
      return '<div class="vg-writelog__field">'
        + '<code class="vg-writelog__fieldname">' + escapeHtml(f.field) + '</code>'
        + (before ? '<span class="vg-writelog__before">' + escapeHtml(before) + '</span>' : '<span class="vg-writelog__before vg-writelog__before--empty">(empty)</span>')
        + '<span class="vg-writelog__arrow">&rarr;</span>'
        + (after ? '<span class="vg-writelog__after">' + escapeHtml(after) + '</span>' : '<span class="vg-writelog__after vg-writelog__after--empty">(cleared)</span>')
        + '</div>';
    }).join('');
    var more = (e.fieldCount || fields.length) > fields.length
      ? '<div class="vg-writelog__more">' + ((e.fieldCount - fields.length)) + ' more field(s) not shown</div>' : '';
    return '<div class="vg-writelog__entry">'
      + '<div class="vg-writelog__entryhead">'
      + '<span class="vg-writelog__who">' + escapeHtml(e.by || 'unknown') + '</span>'
      /* CUSTOM_PRODUCTRULES_BY_CLAUDE (2026-10-04) - the log now carries edits made on this screen
         (source guide-ui) and through Product Rules (via product-rules) beside the agent's. */
      + '<span class="vg-writelog__role vg-writelog__role--' + escapeHtml(e.source === 'guide-ui' ? 'guide-ui' : (e.role || 'admin')) + '">'
      + escapeHtml(e.source === 'guide-ui' ? 'Guide screen' : (e.via === 'claude-session' ? 'Claude session' : (e.via === 'product-rules' ? 'Product Rules' : (e.role === 'agent' ? 'AI agent' : 'admin')))) + '</span>'
      + '<span class="vg-writelog__what">' + (e.mode === 'create' ? 'created' : 'edited') + ' <strong>' + escapeHtml(e.vendorName || e.vendorId || '') + '</strong></span>'
      + '<span class="vg-writelog__when">' + escapeHtml(fmtWriteLogTime(e.at)) + '</span>'
      + '</div>'
      + (e.note ? '<div class="vg-writelog__note-line">' + escapeHtml(e.note) + '</div>' : '')
      + (e.askedBy ? '<div class="vg-writelog__note-line">Asked by: ' + escapeHtml(e.askedBy) + '</div>' : '') // CUSTOM_VENDORHISTORY_BY_CLAUDE
      + (e.expired ? '<div class="vg-writelog__more">The detail of this change has aged out.</div>' : rows + more)
      + '</div>';
  }

  async function toggleWriteLog(btn) {
    if (!els.writeLog) return;
    var open = !els.writeLog.hidden;
    if (open) {
      vgSlide(els.writeLog, false); // CUSTOM_VGUX_BY_CLAUDE (#3)
      btn.setAttribute('aria-expanded', 'false');
      return;
    }
    vgSlide(els.writeLog, true); // CUSTOM_VGUX_BY_CLAUDE (#3)
    btn.setAttribute('aria-expanded', 'true');
    els.writeLogBody.innerHTML = '<div class="vg-writelog__msg">Loading&hellip;</div>';
    try {
      var session = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession && window.RepPortal.Auth.getSession();
      var token = session && session.accessToken;
      if (!token) throw new Error('Not authenticated');
      var res = await fetch(RFQ_WORKER_URL + '/vendor-guide/write-log?limit=25', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.error || 'HTTP ' + res.status);
      var entries = data.entries || [];
      /* ⛔ "Nothing has been changed" and "we could not load this" must not look the same — an
         empty panel that means a failed request is the one that gets ignored. */
      els.writeLogBody.innerHTML = (data.guideUi && data.guideUi !== 'ok'
          ? '<div class="vg-writelog__msg vg-writelog__msg--err">Edits made on this screen could not be loaded (' + escapeHtml(data.guideUi) + '); the rest are below.</div>'
          : '')
        + (entries.length
          ? entries.map(renderWriteLogEntry).join('')
          : '<div class="vg-writelog__msg">No edits recorded yet.</div>');
    } catch (err) {
      els.writeLogBody.innerHTML = '<div class="vg-writelog__msg vg-writelog__msg--err">Could not load the change log: '
        + escapeHtml(err.message || String(err)) + '</div>';
    }
  }
  /* END CUSTOM_GUIDEWRITE_BY_CLAUDE */

  // Migration: trigger rfq-worker's /merge-po-into-guide endpoint, which writes the
  // 8 hardcoded seed addresses onto matching Vendor Guide vendors. Idempotent.
  async function runPoMigration(btn) {
    if (!confirm('Run one-shot migration: write the 8 seed PO addresses (ECORE, Spirit, Fab, etc.) onto matching Vendor Guide vendors. Existing PO data on those vendors is preserved (only blank fields get filled).\n\nProceed?')) return;
    btn.disabled = true;
    var orig = btn.textContent;
    btn.textContent = 'Migrating…';
    try {
      var session = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession && window.RepPortal.Auth.getSession();
      var token = session && session.accessToken;
      if (!token) throw new Error('Not authenticated');
      var res = await fetch(RFQ_WORKER_URL + '/merge-po-into-guide', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: '{}'
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.error || 'HTTP ' + res.status);
      var lines = [];
      lines.push('Matched + updated: ' + (data.matched || []).length);
      (data.matched || []).forEach(function (m) {
        var tag = m.matchType && m.matchType !== 'exact' ? ' [' + m.matchType + ']' : '';
        lines.push('  ✓ ' + m.seedName + ' → ' + m.vendorName + ' (' + (m.shortCode || '—') + ')' + tag);
      });
      if ((data.notMatched || []).length) {
        lines.push('\nUnmatched (no Vendor Guide vendor close enough to match):');
        data.notMatched.forEach(function (n) {
          lines.push('  ✗ ' + n.name);
          if (n.candidates && n.candidates.length) {
            lines.push('     closest in Vendor Guide:');
            n.candidates.forEach(function (c) { lines.push('       • ' + c.name + ' (similarity ' + c.score + ')'); });
          }
        });
      }
      if ((data.errors || []).length) {
        lines.push('\nErrors:');
        data.errors.forEach(function (e) { lines.push('  ⚠ ' + e.name + ': ' + (e.error || 'unknown')); });
      }
      if ((data.availableVendors || []).length) {
        lines.push('\nAll vendors currently in your Vendor Guide (' + data.availableVendors.length + ' total):');
        data.availableVendors.forEach(function (v) { lines.push('  • ' + v.name); });
      } else {
        lines.push('\nYour Vendor Guide appears to be empty. Add vendors first via Vendor View → + Add Vendor, then re-run the migration.');
      }
      alert(lines.join('\n'));
      // Reload vendor list to reflect new PO data
      await loadAll();
      renderPoProfilesView();
    } catch (err) {
      alert('Migration failed: ' + (err.message || err));
    } finally {
      btn.disabled = false;
      btn.textContent = orig;
    }
  }

  // Admin-only "PO Profiles" view: lists every vendor with PO completeness status,
  // a search box, a one-shot Migration button, and a click-to-edit shortcut that
  // opens the regular vendor edit modal (the PO subsection is part of that modal).
  function renderPoProfilesView() {
    var host = qs('[data-vg-view="po-profiles"]');
    if (!host) return;
    var vendors = (state.vendors || []).slice().sort(function (a, b) {
      return (a.name || '').localeCompare(b.name || '');
    });
    var rows = vendors.map(function (v) {
      var filled = poIsFilled(v.po);
      var po = v.po || {};
      var workflow = po.workflow || '—';
      var workflowLabel = ({
        'po-only': 'PO Only',
        'freight-then-po': 'Freight → PO',
        'web-order': 'Web Order',
        'request-only': 'Request Only',
        'freight-then-request': 'Freight → Request'
      })[workflow] || workflow;
      var statusCls = filled ? 'vg-po-row__status--filled' : 'vg-po-row__status--empty';
      var statusLabel = filled ? 'Filled' : 'Blank';
      var shortCode = po.shortCode || '—';
      /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — contact.email is a LIST. Left to
         implicit toString an array rendered "a@x.com,b@x.com" with no space after the comma;
         join it the way the editor does. */
      /* CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11, #13-#15): the Email column is gone; Products and Ordering ready come
         from rfq-worker GET /product-rules/suppliers (the Brands-view checklist, so the two never disagree). */
      var sup = poSupplierStatus(v.id);
      return '<button type="button" class="vg-po-row" data-vg-po-edit="' + escapeHtml(v.id) + '">' +
        '<span class="vg-po-row__name">' + escapeHtml(v.name) + '</span>' +
        '<span class="vg-po-row__short">' + escapeHtml(shortCode) + '</span>' +
        '<span class="vg-po-row__workflow">' + escapeHtml(workflowLabel) + '</span>' +
        poProductsCell(v, sup) +
        poReadyCell(v, sup) +
        '<span class="vg-po-row__status ' + statusCls + '">' + statusLabel + '</span>' +
      '</button>' + (state.poMissingOpen === v.id && sup ? poMissingHtml(v, sup) : '');
    }).join('');
    var totalFilled = vendors.filter(function (v) { return poIsFilled(v.po); }).length;
    host.innerHTML = '<div class="vg-po-bar">' +
        '<div class="vg-po-bar__stat">' + totalFilled + ' / ' + vendors.length + ' vendors with PO data filled</div>' +
        '<button type="button" class="vg-pill-btn" data-vg-po-migrate>Migrate seed addresses (one-shot)</button>' +
      '</div>' +
      '<div class="vg-po-list-head">' +
        '<span>Vendor</span><span>Code</span><span>Workflow</span><span>Products</span><span>Ordering ready</span><span>Status</span>' +
      '</div>' +
      '<div class="vg-po-list">' + (rows || '<p class="vg-empty__text">No vendors loaded yet.</p>') + '</div>' +
      (state.poStatus && state.poStatus.error ? '<p class="vg-po-note">Products and Ordering ready could not load: ' + escapeHtml(state.poStatus.error) + '. This is a loading problem, not missing data.</p>' : '');
    loadPoSupplierStatus();
  }

  /* CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11, #13 + #15) - each supplier's checklist answers and product count, read once
     a page load from rfq-worker (admin) and kept until a save changes the Guide. */
  function loadPoSupplierStatus(force) {
    if (!isAdmin()) return;
    if (state.poStatus && !force && (state.poStatus.loading || state.poStatus.data || state.poStatus.error)) return;
    state.poStatus = { loading: true };
    var session = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession && window.RepPortal.Auth.getSession();
    var token = session && session.accessToken;
    if (!token) { state.poStatus = { error: 'signed out' }; return; }
    fetch(RFQ_WORKER_URL + '/product-rules/suppliers', { headers: { 'Authorization': 'Bearer ' + token } })
      .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, d: d }; }); })
      .then(function (r) {
        if (!r.ok) throw new Error((r.d && r.d.error) || 'HTTP error');
        var byId = {};
        (r.d.suppliers || []).forEach(function (s) { byId[s.id] = s; });
        state.poStatus = { data: byId };
      })
      .catch(function (err) { state.poStatus = { error: (err && err.message) || 'error' }; })
      .then(function () { if (state.view === 'po-profiles') renderPoProfilesView(); });
  }

  function poSupplierStatus(id) {
    return state.poStatus && state.poStatus.data ? (state.poStatus.data[id] || null) : null;
  }

  function poProductsCell(v, sup) {
    if (!sup) return '<span class="vg-po-row__products vg-po-row__muted">' + (state.poStatus && state.poStatus.loading ? '…' : '—') + '</span>';
    var split = (sup.brands || []).map(function (b) { return b.brand + ': ' + b.products; }).join('\n');
    return '<span class="vg-po-row__products" data-vg-po-products="' + escapeHtml(v.id) + '" title="' + escapeHtml(split || 'No live or draft products') + '">' + sup.products + '</span>';
  }

  function poReadyCell(v, sup) {
    if (!sup) return '<span class="vg-po-row__ready vg-po-row__muted">' + (state.poStatus && state.poStatus.loading ? '…' : '—') + '</span>';
    var g = sup.guide || {};
    var cls = g.complete ? 'vg-po-ready--ok' : 'vg-po-ready--gap';
    var text = g.complete ? '✓ Ready' : (g.done + '/' + g.needed);
    return '<span class="vg-po-row__ready"><span class="vg-po-ready ' + cls + '" data-vg-po-ready="' + escapeHtml(v.id) + '" title="' +
      escapeHtml(g.complete ? 'Every answer that applies to this supplier is in.' : 'Click to see what is missing') + '">' + text + '</span></span>';
  }

  function poMissingHtml(v, sup) {
    var miss = (sup.guide && sup.guide.missing) || [];
    return '<div class="vg-po-missing">' +
      (miss.length
        ? '<p class="vg-po-missing__title">Still needed for ' + escapeHtml(v.name) + ':</p><ul>' + miss.map(function (m) {
            return '<li><strong>' + escapeHtml(m.label) + '</strong>' + (m.why ? ' <span>(' + escapeHtml(m.why) + ')</span>' : '') + '</li>';
          }).join('') + '</ul>'
        : '<p class="vg-po-missing__title">Nothing missing.</p>') +
      '<button type="button" class="vg-pill-btn vg-pill-btn--primary" data-vg-po-edit="' + escapeHtml(v.id) + '">Open ' + escapeHtml(v.name) + '</button>' +
    '</div>';
  }

  function enterCategory(catKey) {
    state.selectedCategoryKey = catKey;
    state.selectedSubcategoryKey = null;
    renderSubcategoryList(catKey);
    showCategoryLevel('subcat');
  }

  function enterSubcategory(subKey) {
    state.selectedSubcategoryKey = subKey;
    renderRankedVendors(subKey);
    showCategoryLevel('ranked');
  }

  // ------------------------------------------------------------------
  // Header (rep name)
  // ------------------------------------------------------------------
  function populateHeader() {
    var session = getSession();
    var el = qs('.rep-quote-header__rep-name');
    if (el && session) {
      el.textContent = session.name || session.code || '';
    }
  }

  // ------------------------------------------------------------------
  // Load
  // ------------------------------------------------------------------
  async function loadAll() {
    showLoading();
    try {
      var tax = await apiFetch('/taxonomy');
      var resp = await apiFetch('/vendors');
      state.taxonomy = tax;
      state.vendors = ((resp && resp.vendors) || []).map(function (v) {
        if (!v.availability && v.customColors) v.availability = 'custom';
        return v;
      });
      hideLoading();
      populateCategoryFilterOptions();
      setView(state.view);
      openVendorFromHash(); // CUSTOM_PRODUCTRULES_BY_CLAUDE - Product Rules' "Open in Vendor Guide"
    } catch (err) {
      showError(err && err.message ? err.message : 'Unknown error');
    }
  }

  /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Product Rules Session 10) - what the fill-from-text box
     (assets/custom-vendor-guide-fill.js) reads: the supplier list, a reload after a save, and whether
     the viewer is an admin. Read on each use, so it always sees the current list. */
  /* CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11, UX batch #3) - open / close a panel with a slide instead of a snap. The
     `hidden` attribute is still the truth (set before the slide opens, after it closes), so every check on it keeps
     working. Reduced motion, or a panel already in the wanted state, just sets it. */
  function vgSlide(el, open) {
    if (!el) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (el._vgSlideEnd) { el.removeEventListener('transitionend', el._vgSlideEnd); el._vgSlideEnd = null; }
    if (reduce || (!!open === !el.hidden && !el.classList.contains('vg-slide'))) {
      el.hidden = !open; el.style.height = ''; el.style.opacity = ''; el.classList.remove('vg-slide'); return;
    }
    el.classList.add('vg-slide');
    if (open) {
      el.hidden = false;
      var h = el.scrollHeight;
      el.style.height = '0px'; el.style.opacity = '0';
      void el.offsetHeight;
      el.style.height = h + 'px'; el.style.opacity = '1';
    } else {
      el.style.height = el.scrollHeight + 'px';
      void el.offsetHeight;
      el.style.height = '0px'; el.style.opacity = '0';
    }
    el._vgSlideEnd = function (e) {
      if (e && e.target !== el) return;
      el.removeEventListener('transitionend', el._vgSlideEnd); el._vgSlideEnd = null;
      if (!open) el.hidden = true;
      el.style.height = ''; el.style.opacity = ''; el.classList.remove('vg-slide');
    };
    el.addEventListener('transitionend', el._vgSlideEnd);
    // A transition that never reports its end (a hidden tab, a browser that skips it) must not leave the panel
    // clipped at the height it opened with: finish it anyway shortly after it should have ended.
    var end = el._vgSlideEnd;
    setTimeout(function () { if (el._vgSlideEnd === end) end(); }, 750);
  }
  window.VGSlide = vgSlide;

  window.VendorGuideHooks = {
    vendors: function () { return state.vendors || []; },
    reload: function () { return loadAll(); },
    isAdmin: function () { return isAdmin(); },
  };

  /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Ryan 2026-10-04) - `#v=<vendor id>` opens that vendor's card, so
     Product Rules can link straight to the record it edits. An unknown id opens nothing. */
  function openVendorFromHash() {
    var m = String(window.location.hash || '').match(/[#&]v=([^&]+)/);
    if (!m) return;
    var id = decodeURIComponent(m[1]);
    if (vendorById(id)) openVendorModal(id, 'view');
  }

  function showLoading() {
    els.loading.hidden = false;
    els.error.hidden = true;
    qsa('[data-vg-view]').forEach(function (el) { el.hidden = true; });
  }

  function hideLoading() {
    els.loading.hidden = true;
    els.error.hidden = true;
  }

  function showError(msg) {
    els.loading.hidden = true;
    els.error.hidden = false;
    els.errorDetail.textContent = msg || '';
    qsa('[data-vg-view]').forEach(function (el) { el.hidden = true; });
  }

  // --- Transient toasts (non-blocking) ---
  function toastStack() {
    var stack = qs('[data-vg-toasts]');
    if (!stack) {
      var wrap = qs('.vg-wrap');
      if (!wrap) return null;
      stack = document.createElement('div');
      stack.className = 'vg-toasts';
      stack.setAttribute('data-vg-toasts', '');
      stack.setAttribute('role', 'status');
      stack.setAttribute('aria-live', 'polite');
      wrap.appendChild(stack);
    }
    return stack;
  }

  function showToast(message, kind, ms) {
    var stack = toastStack();
    if (!stack) return;
    var t = document.createElement('div');
    t.className = 'vg-toast vg-toast--' + (kind || 'info');
    t.textContent = message;
    stack.appendChild(t);
    // Fade in on next frame
    requestAnimationFrame(function () { t.classList.add('vg-toast--in'); });
    var life = ms || (kind === 'error' ? 5500 : 3500);
    setTimeout(function () {
      t.classList.remove('vg-toast--in');
      setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 250);
    }, life);
  }

  function errMsg(err, fallback) {
    return (err && err.message) ? err.message : (fallback || 'Unknown error');
  }

  // ------------------------------------------------------------------
  // Admin surface — toggle + add button visibility
  // ------------------------------------------------------------------
  function initAdminSurface() {
    if (!isAdmin()) return;
    if (els.adminStrip) els.adminStrip.hidden = false;
    // Reveal admin-only tabs/buttons (PO Profiles, etc.)
    qsa('[data-vg-admin-only]').forEach(function (el) { el.hidden = false; });
    state.adminMode = readAdminModePref(); // CUSTOM_PRODUCTRULES_BY_CLAUDE: opens the way you last left it
    applyAdminMode();
  }

  /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Ryan 2026-10-11) - Admin Mode is remembered per browser: on if you left it on, off
     otherwise. A blocked or empty storage reads as off (the page's old default). */
  var ADMIN_MODE_KEY = 'vg-admin-mode';
  function readAdminModePref() {
    try { return window.localStorage.getItem(ADMIN_MODE_KEY) === 'on'; } catch (e) { return false; }
  }
  function saveAdminModePref(on) {
    try { window.localStorage.setItem(ADMIN_MODE_KEY, on ? 'on' : 'off'); } catch (e) { /* not remembered; nothing else changes */ }
  }

  function applyAdminMode() {
    var wrap = qs('.vg-wrap');
    if (wrap) wrap.classList.toggle('vg-admin-mode', state.adminMode && isAdmin());
    if (els.adminToggle) els.adminToggle.setAttribute('aria-pressed', state.adminMode ? 'true' : 'false');
    if (els.adminBanner) els.adminBanner.hidden = !(state.adminMode && isAdmin());
    if (els.adminBar) els.adminBar.hidden = !(state.adminMode && isAdmin());
    /* CUSTOM_GUIDEWRITE_BY_CLAUDE — the log panel lives inside admin mode, so leaving admin mode
       must close it; otherwise it stays on screen with the button that opened it gone. */
    /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Session 10) - the fill box lives in admin mode too. */
    var _fill = qs('[data-vg-fill]');
    if (_fill && !(state.adminMode && isAdmin())) {
      _fill.hidden = true;
      var _fillBtn = qs('[data-vg-fill-toggle]');
      if (_fillBtn) _fillBtn.setAttribute('aria-expanded', 'false');
    }
    if (els.recipientsPanel && !(state.adminMode && isAdmin())) { // CUSTOM_VGUX_BY_CLAUDE: the upload-email panel too
      els.recipientsPanel.hidden = true;
      var _rcBtn = qs('[data-vg-recipients-toggle]');
      if (_rcBtn) _rcBtn.setAttribute('aria-expanded', 'false');
    }
    if (els.writeLog && !(state.adminMode && isAdmin())) {
      els.writeLog.hidden = true;
      var _wlBtn = qs('[data-vg-writelog-toggle]');
      if (_wlBtn) _wlBtn.setAttribute('aria-expanded', 'false');
    }
  }

  function toggleAdminMode() {
    if (!isAdmin()) return;
    state.adminMode = !state.adminMode;
    saveAdminModePref(state.adminMode);
    applyAdminMode();
    // Re-render both views so the pencil edit buttons appear/disappear
    if (state.view === 'category') {
      if (state.selectedSubcategoryKey) renderRankedVendors(state.selectedSubcategoryKey);
      else if (state.selectedCategoryKey) renderSubcategoryList(state.selectedCategoryKey);
      else renderCategoryTiles();
    } else {
      renderVendorView();
    }
  }

  // ------------------------------------------------------------------
  // Vendor modal — draft, rendering, save, delete
  // ------------------------------------------------------------------
  function blankVendorDraft() {
    var d = {
      id: null,
      name: '',
      logoUrl: '',
      leadTime: '',
      tiers: [],
      margins: [],
      websiteUrl: '',
      categoryPageUrl: '',
      notes: '',
      availability: '',
      confidential: false,
      defunct: false,
      madeByHand: false, // CUSTOM_BUILTTOORDER_BY_CLAUDE
      usuallyInStock: false, // CUSTOM_USUALLYINSTOCK_BY_CLAUDE
      placements: [],
      documents: [],
      po: blankPo()
    };
    initPlacementsUi(d);
    return d;
  }

  function blankPo() {
    return {
      shortCode: '',
      workflow: '',
      // CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — names this vendor goes by on our quotes.
      quoteAliases: [],
      // CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-27) — product words that pick this vendor out
      // of two claiming the same alias. Globally unique; the worker refuses a save that repeats one.
      aliasKeywords: [],
      // CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — '' = the rep's saved default decides.
      poFrom: '',
      address: { street: '', cityStateZip: '' },
      // CUSTOM_VENDORGREET_BY_CLAUDE (AIOPS 0c175f74a4f9) — ordersContactFirstName = the order
      // desk's first name for Abby's "Hi Carlene,"; attention stays the printed Attn: line.
      contact: { attention: '', ordersContactFirstName: '', ordersGreeting: '', phone: '', email: [], cc: [], bcc: [] },
      paymentTerms: '',
      // CUSTOM_POSTPOWAIT_BY_CLAUDE (AIOPS 82b958ec5f02, Ryan 2026-09-12) — none | ack |
      // payment-link. 'none' is the behaviour every vendor had before the field existed, and the
      // default a new vendor must start at: a wait opened by accident is a chase nobody asked for.
      postPoWait: 'none',
      // CUSTOM_PRODUCTRULES_BY_CLAUDE (Session 8) - how we pay, and their dealer portal (the login's NAME only).
      payMethod: '', dealerPortalUrl: '', dealerPortalAccess: '', dealerPortalLogin: '',
      specialInstructions: '',
      defaultInstructionsVendor: '',
      defaultInstructionsBsfs: '',
      emailSubject: 'Blue Sky Order | {ZIP} | {PO}',
      emailBody: '',
      emailBodyVendor: '',
      emailBodyBsfs: '',
      // CUSTOM_RFQPORTAL_BY_CLAUDE — per-vendor freight-request verbiage (freight-then-po only)
      freightDimsSubject: '',
      freightDimsBody: '',
      freightQuoteSubject: '',
      freightQuoteBody: '',
      // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — pallet / packaging fee, charged only when
      // Blue Sky brokers the freight. 0 = this vendor doesn't charge one.
      palletFee: 0,
      palletFeeBasis: 'per-product',
      palletFeeLabel: '',
      // (Ryan 2026-09-19) product handles this vendor never charges the pallet fee on.
      palletFeeExclusions: []
    };
  }

  function poIsFilled(po) {
    if (!po) return false;
    var email = po.contact && po.contact.email;
    var hasEmail = Array.isArray(email) ? email.length > 0 : !!email;
    return !!(po.shortCode || hasEmail || (po.address && po.address.street));
  }

  // Derive UI state for the multi-select placements editor from draft.placements.
  // _ui lives on the draft, never gets sent to the worker.
  function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    var sa = a.slice().sort(), sb = b.slice().sort();
    for (var i = 0; i < sa.length; i++) { if (sa[i] !== sb[i]) return false; }
    return true;
  }

  function initPlacementsUi(draft) {
    var placements = draft.placements || [];
    var defTiers = (draft.tiers && draft.tiers.length) ? draft.tiers : ['best'];
    var defMargins = (draft.margins && draft.margins.length) ? draft.margins : [3];
    var selected = {};
    var overrides = {};
    placements.forEach(function (p) {
      if (!p || !p.subcategoryKey) return;
      selected[p.subcategoryKey] = true;
      var pTiers = p.tiers || (p.tier ? [String(p.tier).toLowerCase()] : []);
      var pMargins = p.margins || (p.margin ? [Number(p.margin)] : []);
      if (pTiers.length && !arraysEqual(pTiers, defTiers) || pMargins.length && !arraysEqual(pMargins, defMargins)) {
        overrides[p.subcategoryKey] = { tiers: pTiers, margins: pMargins };
      }
    });
    draft._ui = {
      defaultTiers: defTiers,
      defaultMargins: defMargins,
      selected: selected,
      overrides: overrides
    };
  }

  function openVendorModal(vendorId, mode) {
    if (!els.modal) return;
    state.modal.open = true;
    state.modal.mode = mode || 'view';
    state.modal.vendorId = vendorId || null;
    state.modal.activeTab = 'info';

    if (mode === 'new') {
      state.modal.draft = blankVendorDraft();
    } else if (vendorId) {
      var v = vendorById(vendorId);
      if (!v) { closeVendorModal(); return; }
      // deep-ish clone — draft is separate from state until save
      state.modal.draft = JSON.parse(JSON.stringify(v));
      if (!state.modal.draft.availability && state.modal.draft.customColors) state.modal.draft.availability = 'custom';
      if (!state.modal.draft.placements) state.modal.draft.placements = [];
      if (!state.modal.draft.documents) state.modal.draft.documents = [];
      initPlacementsUi(state.modal.draft);
    }

    els.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    renderModal();

  }

  /* CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11, UX batch #12) - an edit with unsaved changes asks before the pop-up closes
     (Cancel, the X, a click outside, Escape). Save keeps the pop-up open, so this is the one way to lose work. */
  /* CUSTOM_PERPRODUCTFREIGHT_BY_CLAUDE (Ryan 2026-10-11) - which freight rows show. The flat fee shows for a flat-rate
     supplier charged per order; the rates-per-product list shows for a locked-rate supplier (with its basis and per-order
     amount) or a flat-rate supplier charged per product (the list alone, relabelled for the PO). */
  function syncFreightRows() {
    var root = els.modalBody;
    if (!root) return;
    var val = function (id) { var el = root.querySelector('#' + id); return el ? el.value : ''; };
    var show = function (sel, on) { var el = root.querySelector(sel); if (el) el.style.display = on ? '' : 'none'; };
    var flat = val('vg-po-shippingModel') === 'flat-rate';
    var flatPP = flat && val('vg-po-flatRateBasis') === 'per-product';
    var locked = val('vg-po-freightMode') === 'locked-rate';
    show('[data-vg-po-flat-rate-row]', flat);
    show('[data-vg-po-flat-amount]', flat && !flatPP);
    show('[data-vg-po-locked-row]', locked || flatPP);
    show('[data-vg-po-locked-only]', locked);
    var lab = root.querySelector('[data-vg-po-rates-label]');
    if (lab) lab.firstChild.textContent = (flatPP && !locked)
      ? 'Their freight rate per product, billed on the PO (one per line: SKU = 45)'
      : 'Rates per product (one per line: SKU = 350; pallet 20x78x48, 315 lb)';
  }

  function requestCloseVendorModal() {
    var editing = state.modal.mode === 'edit' || state.modal.mode === 'new';
    if (editing && state.modal.dirty) {
      openConfirm('You have unsaved changes. Close without saving them?', closeVendorModal);
      return;
    }
    closeVendorModal();
  }
  function markModalDirty(e) {
    if (!state.modal.open || (state.modal.mode !== 'edit' && state.modal.mode !== 'new')) return;
    var t = e && e.target;
    // Searches, the copy-to-others picks and the recipient switches are not edits to this record (a recipient
    // switch saves itself).
    if (t && t.closest && t.closest('[data-vg-pfx-search], .vg-po-copy, [data-vg-recipient-toggle], [data-vg-fill]')) return;
    if (state.modal.dirty) return;
    state.modal.dirty = true;
    var flag = els.modalFooter && els.modalFooter.querySelector('[data-vg-dirty]');
    if (flag) flag.hidden = false;
  }
  /* END CUSTOM_VGUX_BY_CLAUDE */

  function closeVendorModal() {
    if (window.VGPoForm) window.VGPoForm.closeAll();
    state.modal.dirty = false;
    state.modal.open = false;
    state.modal.mode = null;
    state.modal.vendorId = null;
    state.modal.draft = null;
    state.modal.activeTab = 'info';
    if (els.modal) els.modal.hidden = true;
    document.body.style.overflow = '';
  }

  function renderModal() {
    if (!els.modal || !state.modal.open) return;
    var mode = state.modal.mode;
    var d = state.modal.draft;

    // Title
    if (mode === 'new') els.modalTitle.textContent = 'New Vendor';
    else if (mode === 'edit') els.modalTitle.textContent = 'Edit ' + ((d && d.name) || 'Vendor'); // CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11): name the supplier
    else els.modalTitle.textContent = d ? d.name : 'Vendor';

    // Body
    els.modalBody.innerHTML = (mode === 'view') ? renderViewBody(d) : renderEditBody(d, mode);
    if (mode === 'view' && d && d.id && isAdmin()) loadVendorHistory(d.id); // CUSTOM_VENDORHISTORY_BY_CLAUDE

    // Footer
    els.modalFooter.innerHTML = renderFooter(mode);

    /* CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11, UX batch push 2) - a fresh render holds the saved record, so nothing is
       unsaved; an edit gets the wide pop-up (#8) and the PO Profile's cards, tips and dropdowns (#5-#12,
       custom-vendor-guide-po-form.js). */
    state.modal.dirty = false;
    els.modal.classList.toggle('vg-modal--wide', mode === 'edit' || mode === 'new');
    if (mode === 'edit' || mode === 'new') syncFreightRows(); // CUSTOM_PERPRODUCTFREIGHT_BY_CLAUDE
    if ((mode === 'edit' || mode === 'new') && isAdmin() && window.VGPoForm) {
      try { window.VGPoForm.closeAll(); window.VGPoForm.enhance(els.modalBody); }
      catch (err) { if (window.console) console.warn('PO Profile layout failed; the plain form is still here.', err); }
    }
    /* END CUSTOM_VGUX_BY_CLAUDE */

    // Help button — admin edit/new only
    var showHelp = isAdmin() && (mode === 'edit' || mode === 'new');
    if (els.modalHelp) els.modalHelp.hidden = !showHelp;
    if (!showHelp) closeModalHelp();
  }

  function toggleModalHelp() {
    if (!els.modalHelp || !els.modalHelpPop) return;
    var open = !els.modalHelpPop.hidden ? false : true;
    if (open) {
      els.modalHelpPop.hidden = false;
      els.modalHelp.setAttribute('aria-expanded', 'true');
    } else {
      closeModalHelp();
    }
  }

  function closeModalHelp() {
    if (!els.modalHelp || !els.modalHelpPop) return;
    els.modalHelpPop.hidden = true;
    els.modalHelp.setAttribute('aria-expanded', 'false');
  }

  function renderViewBody(v) {
    if (!v) return '';
    var badges = [];
    ['good', 'better', 'best'].forEach(function (t) {
      if (v.tiers && v.tiers.indexOf(t) !== -1) badges.push('<span class="vg-badge vg-badge--tier-' + t + '">' + TIER_LABELS[t] + '</span>');
    });
    if (v.confidential) {
      badges.push('<span class="vg-badge vg-badge--conf">REQUEST PRICING</span>');
    } else if (v.margins && v.margins.length) {
      var mRangeHtml = marginRangeHtml(v.margins);
      if (mRangeHtml) badges.push(mRangeHtml);
    }
    if (v.defunct) badges.push('<span class="vg-badge vg-badge--defunct">Out of business</span>');
    if (v.availability === 'custom') badges.push('<span class="vg-badge vg-badge--custom">Custom</span>');
    else if (v.availability === 'in-stock') badges.push('<span class="vg-badge vg-badge--instock">In-stock</span>');
    if (v.usuallyInStock) badges.push('<span class="vg-badge vg-badge--instock">Usually in stock</span>'); // CUSTOM_USUALLYINSTOCK_BY_CLAUDE
    if (v.leadTime) badges.push('<span class="vg-badge vg-badge--lead">' + escapeHtml(v.leadTime) + '</span>');

    var links = [];
    if (v.websiteUrl) links.push('<a class="vg-profile-link" href="' + escapeHtml(v.websiteUrl) + '" target="_blank" rel="noopener noreferrer"><span class="vg-profile-link__icon" aria-hidden="true">&#127760;</span>Vendor website</a>');
    if (v.categoryPageUrl) links.push('<a class="vg-profile-link" href="' + escapeHtml(v.categoryPageUrl) + '" target="_blank" rel="noopener noreferrer"><span class="vg-profile-link__icon" aria-hidden="true">&#128722;</span>Our catalog page</a>');

    var head =
      '<div class="vg-profile-head">' +
        renderLogo(v) +
        '<div class="vg-profile-head__info">' +
          '<h3 class="vg-profile-head__name">' + escapeHtml(v.name) + '</h3>' +
          (badges.length ? '<div class="vg-profile-head__badges">' + badges.join('') + '</div>' : '') +
          (links.length ? '<div class="vg-profile-head__links">' + links.join('') + '</div>' : '') +
        '</div>' +
      '</div>';

    // Placements grouped by category for readability
    var placementsHtml;
    if (!v.placements || !v.placements.length) {
      placementsHtml = '<p class="vg-empty__text">No placements yet.</p>';
    } else {
      var catGroups = {};
      var catOrder = [];
      v.placements.forEach(function (p) {
        var info = subcategoryByKey(p.subcategoryKey);
        var catName = info ? info.category.name : '—';
        if (!catGroups[catName]) { catGroups[catName] = []; catOrder.push(catName); }
        catGroups[catName].push(p);
      });
      placementsHtml = '<div class="vg-placement-list">' +
        catOrder.map(function (catName) {
          var group = catGroups[catName];

          function badgeKey(p) {
            var pTiers = (p.tiers || (p.tier ? [String(p.tier).toLowerCase()] : [])).slice().sort().join(',');
            var pMargins = (p.margins || (p.margin ? [Number(p.margin)] : [])).slice().sort(function (a, b) { return a - b; }).join(',');
            return pTiers + '|' + pMargins;
          }
          var allSame = group.length > 1 && group.every(function (p) { return badgeKey(p) === badgeKey(group[0]); });

          var catBadges = '';
          if (allSame) {
            var ref = group[0];
            var refTiers = ref.tiers || (ref.tier ? [String(ref.tier).toLowerCase()] : []);
            var refMargins = ref.margins || (ref.margin ? [Number(ref.margin)] : []);
            ['good', 'better', 'best'].forEach(function (t) {
              if (refTiers.indexOf(t) !== -1) catBadges += '<span class="vg-badge vg-badge--tier-' + t + '">' + TIER_LABELS[t] + '</span>';
            });
            catBadges += marginRangeHtml(refMargins);
          }

          var subs = group.map(function (p) {
            var info = subcategoryByKey(p.subcategoryKey);
            var subName = info ? info.subcategory.name : p.subcategoryKey;
            if (allSame) {
              return '<span class="vg-placement-sub">' + escapeHtml(subName) + '</span>';
            }
            var pTiers = p.tiers || (p.tier ? [String(p.tier).toLowerCase()] : []);
            var pMargins = p.margins || (p.margin ? [Number(p.margin)] : []);
            var tierBadges = '';
            ['good', 'better', 'best'].forEach(function (t) {
              if (pTiers.indexOf(t) !== -1) tierBadges += '<span class="vg-badge vg-badge--tier-' + t + '">' + TIER_LABELS[t] + '</span>';
            });
            var marginBadge = marginRangeHtml(pMargins);
            return '<span class="vg-placement-sub">' + escapeHtml(subName) + tierBadges + marginBadge + '</span>';
          }).join('');
          return (
            '<div class="vg-placement-group">' +
              '<div class="vg-placement-group__cat">' + escapeHtml(catName) + catBadges + '</div>' +
              '<div class="vg-placement-group__subs">' + subs + '</div>' +
            '</div>'
          );
        }).join('') +
      '</div>';
    }

    // Documents (respect confidential gate for non-admin)
    var docsHtml;
    if (v.confidential && !isAdmin()) {
      docsHtml = '<div class="vg-conf-notice">' +
        '<span class="vg-conf-notice__icon" aria-hidden="true">&#128274;</span>' +
        '<span>Documents for this vendor are restricted. Contact an admin for access.</span>' +
      '</div>';
    } else if (!v.documents || !v.documents.length) {
      docsHtml = '<p class="vg-empty__text">No documents attached.</p>';
    } else {
      docsHtml = '<div class="vg-doc-list">' +
        v.documents.map(function (doc) {
          return (
            '<div class="vg-doc-item">' +
              '<a class="vg-doc-item__name" href="' + escapeHtml(doc.url) + '" target="_blank" rel="noopener noreferrer">' +
                escapeHtml(doc.name) +
              '</a>' +
              (doc.type ? '<span class="vg-doc-item__type">' + escapeHtml(doc.type) + '</span>' : '') +
            '</div>'
          );
        }).join('') +
      '</div>';
    }

    var notesHtml = v.notes
      ? '<div class="vg-notes">' + escapeHtml(v.notes) + '</div>'
      : '<p class="vg-empty__text">No notes.</p>';

    return head +
      '<div class="vg-modal__section">' +
        '<h4 class="vg-modal__section-title">Categories</h4>' + placementsHtml +
      '</div>' +
      '<div class="vg-modal__section">' +
        '<h4 class="vg-modal__section-title">Documents</h4>' + docsHtml +
      '</div>' +
      '<div class="vg-modal__section">' +
        '<h4 class="vg-modal__section-title">Notes</h4>' + notesHtml +
      '</div>' +
      /* CUSTOM_VENDORHISTORY_BY_CLAUDE (ALFRED e30e411a8844, Ryan 2026-10-17: admins only) - this supplier's change
         history, filled by loadVendorHistory() once the modal is on screen. */
      (isAdmin() && v.id
        ? '<div class="vg-modal__section"><h4 class="vg-modal__section-title">Change history</h4>' +
          '<div class="vg-writelog__body" data-vg-history="' + escapeHtml(v.id) + '"><div class="vg-writelog__msg">Loading&hellip;</div></div></div>'
        : '');
  }

  /* CUSTOM_VENDORHISTORY_BY_CLAUDE - one supplier's history: the same GET /vendor-guide/write-log the Recent changes
     panel reads, filtered to this vendor id (agent and Product Rules edits plus edits made on this screen). */
  async function loadVendorHistory(vendorId) {
    var box = els.modalBody && els.modalBody.querySelector('[data-vg-history]');
    if (!box) return;
    try {
      var session = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession && window.RepPortal.Auth.getSession();
      var token = session && session.accessToken;
      if (!token) throw new Error('Not authenticated');
      var res = await fetch(RFQ_WORKER_URL + '/vendor-guide/write-log?limit=50&vendor=' + encodeURIComponent(vendorId), {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      var data = await res.json();
      if (!res.ok) throw new Error(data.error || 'HTTP ' + res.status);
      if (box.getAttribute('data-vg-history') !== vendorId || !box.isConnected) return;
      var entries = data.entries || [];
      box.innerHTML = (data.guideUi && data.guideUi !== 'ok'
          ? '<div class="vg-writelog__msg vg-writelog__msg--err">Edits made on this screen could not be loaded (' + escapeHtml(data.guideUi) + '); the rest are below.</div>'
          : '')
        + (entries.length ? entries.map(renderWriteLogEntry).join('') : '<div class="vg-writelog__msg">No changes recorded for this supplier yet.</div>');
    } catch (err) {
      if (box.isConnected) box.innerHTML = '<div class="vg-writelog__msg vg-writelog__msg--err">Could not load the change history: ' + escapeHtml(err.message || String(err)) + '</div>';
    }
  }
  /* END CUSTOM_VENDORHISTORY_BY_CLAUDE */

  function renderEditBody(d, mode) {
    if (!d) d = blankVendorDraft();

    var basics =
      '<div class="vg-modal__section">' +
        '<h4 class="vg-modal__section-title">Basics</h4>' +
        // CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11): two fields a row now the pop-up is wide (one a row on a phone)
        '<div class="vg-form__grid2">' +
        '<div class="vg-form__row">' +
          '<label class="vg-form__label" for="vg-f-name">Vendor name *</label>' +
          '<input class="vg-form__input" id="vg-f-name" data-vg-field="name" type="text" value="' + escapeHtml(d.name) + '" placeholder="e.g., Rogue Fitness" />' +
        '</div>' +
        '<div class="vg-form__row">' +
          '<label class="vg-form__label" for="vg-f-lead">Lead time</label>' +
          '<input class="vg-form__input" id="vg-f-lead" data-vg-field="leadTime" type="text" value="' + escapeHtml(d.leadTime || '') + '" placeholder="e.g., 2-4 weeks" />' +
        '</div>' +
        '<div class="vg-form__row">' +
          '<label class="vg-form__label" for="vg-f-website">Vendor website</label>' +
          '<input class="vg-form__input" id="vg-f-website" data-vg-field="websiteUrl" type="url" value="' + escapeHtml(d.websiteUrl || '') + '" placeholder="https://www.vendorsite.com" />' +
        '</div>' +
        '<div class="vg-form__row">' +
          '<label class="vg-form__label" for="vg-f-catpage">Our category page</label>' +
          '<input class="vg-form__input" id="vg-f-catpage" data-vg-field="categoryPageUrl" type="url" value="' + escapeHtml(d.categoryPageUrl || '') + '" placeholder="https://blueskyfitsupply.com/collections/..." />' +
        '</div>' +
        '<div class="vg-form__row">' +
          '<label class="vg-form__label" for="vg-f-logo">Logo URL (Shopify Files)</label>' +
          '<input class="vg-form__input" id="vg-f-logo" data-vg-field="logoUrl" type="url" value="' + escapeHtml(d.logoUrl || '') + '" placeholder="https://cdn.shopify.com/..." />' +
        '</div>' +
        '</div>' +
        '<div class="vg-form__row vg-form__row--inline">' +
          '<span class="vg-form__radio-group">' +
            '<label class="vg-form__checkbox"><input type="radio" name="vg-availability" data-vg-availability value="custom"' + (d.availability === 'custom' ? ' checked' : '') + '> Custom</label>' +
            '<label class="vg-form__checkbox"><input type="radio" name="vg-availability" data-vg-availability value="in-stock"' + (d.availability === 'in-stock' ? ' checked' : '') + '> In-stock</label>' +
          '</span>' +
          /* CUSTOM_BUILTTOORDER_BY_CLAUDE (Ryan 2026-10-13, option 2) - with Custom, the customer's first email says "built to
             order by hand" (Legend) instead of "made to order", with the Lead time above as the supplier's current lead time. */
          '<label class="vg-form__checkbox" title="With Custom: the customer&rsquo;s first email says &quot;built to order by hand&quot; instead of &quot;made to order&quot;."><input type="checkbox" data-vg-field="madeByHand"' + (d.madeByHand ? ' checked' : '') + '> Made by hand</label>' +
          /* CUSTOM_USUALLYINSTOCK_BY_CLAUDE (ONYX 63612a1c4a15) - not the In-stock radio: this one stops the ready-date chases. */
          '<label class="vg-form__checkbox" title="No &quot;when will this be ready?&quot; emails to this supplier. One &quot;has it shipped yet?&quot; check goes 7 business days after we pay, if there is still no tracking. A product can override it on Product Rules."><input type="checkbox" data-vg-field="usuallyInStock"' + (d.usuallyInStock ? ' checked' : '') + '> Usually in stock</label>' +
          '<label class="vg-form__checkbox"><input type="checkbox" data-vg-field="confidential"' + (d.confidential ? ' checked' : '') + '> Confidential (pricing on request)</label>' +
          '<label class="vg-form__checkbox"><input type="checkbox" data-vg-field="defunct"' + (d.defunct ? ' checked' : '') + '> Out of business</label>' +
        '</div>' +
      '</div>';

    var placements =
      '<div class="vg-modal__section">' +
        '<h4 class="vg-modal__section-title">Categories</h4>' +
        '<div data-vg-placements>' + renderPlacementsEditor(d) + '</div>' +
      '</div>';

    // Docs only make sense after the vendor exists
    var docs;
    if (mode === 'new') {
      docs =
        '<div class="vg-modal__section">' +
          '<h4 class="vg-modal__section-title">Documents</h4>' +
          '<p class="vg-empty__text">Save the vendor first to attach documents.</p>' +
        '</div>';
    } else {
      docs =
        '<div class="vg-modal__section">' +
          '<h4 class="vg-modal__section-title">Documents</h4>' +
          '<div data-vg-docs>' + renderDocsEditor(d) + '</div>' +
          renderDocAddForm() +
        '</div>';
    }

    var notes =
      '<div class="vg-modal__section">' +
        '<h4 class="vg-modal__section-title">Notes</h4>' +
        '<textarea class="vg-form__textarea" data-vg-field="notes" rows="4" placeholder="Internal notes — contacts, quirks, anything reps should know...">' + escapeHtml(d.notes || '') + '</textarea>' +
      '</div>';

    // PO Profile — admin only. Drives the rfq-worker's PO PDF + email flow.
    // Empty by default; vendors that don't get POs (Hyperice/ANCORE/etc.) just leave
    // workflow as 'request-only'.
    var poSection = '';
    if (isAdmin()) poSection = renderPoSection(d);

    // Notification Recipients — edit mode only, once the vendor exists (needs id for test-notification).
    // CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11): the upload-email list moved to the admin bar ("Upload emails").
    var recipients = '';

    /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE — Split the modal body into two tabs to
       reduce scroll length and make the admin PO setup easier to follow.
       Tab 1 (Vendor Info): basics + categories + docs + notes.
       Tab 2 (PO Profile):  PO Profile + Notification Recipients.
       Non-admins / view mode see no PO sections, so no tab bar is needed —
       the body renders flat. Both tabs are kept in the DOM at all times
       (one is just hidden) so collectVendorFromForm can read every field
       regardless of which tab is active when Save is clicked. */
    var hasPoTab = isAdmin() && (poSection || recipients);
    if (!hasPoTab) {
      return basics + placements + docs + notes;
    }
    var activeTab = (state.modal && state.modal.activeTab === 'po') ? 'po' : 'info';
    return (
      '<div class="vg-modal__tabs" role="tablist">' +
        '<button type="button" class="vg-modal__tab' + (activeTab === 'info' ? ' vg-modal__tab--active' : '') + '" data-vg-modal-tab="info" role="tab" aria-selected="' + (activeTab === 'info' ? 'true' : 'false') + '">Vendor Info</button>' +
        '<button type="button" class="vg-modal__tab' + (activeTab === 'po'   ? ' vg-modal__tab--active' : '') + '" data-vg-modal-tab="po"   role="tab" aria-selected="' + (activeTab === 'po'   ? 'true' : 'false') + '">PO Profile <span class="vg-po-badge" title="Admin only — drives PO PDF + email">Admin</span></button>' +
      '</div>' +
      '<div class="vg-modal__tabpanel" data-vg-modal-tabpanel="info"' + (activeTab === 'info' ? '' : ' hidden') + '>' +
        basics + placements + '<div class="vg-modal__pair">' + docs + notes + '</div>' + // CUSTOM_VGUX_BY_CLAUDE: side by side when wide
      '</div>' +
      '<div class="vg-modal__tabpanel" data-vg-modal-tabpanel="po"' + (activeTab === 'po' ? '' : ' hidden') + '>' +
        poSection + recipients +
      '</div>'
    );
    /* END CUSTOM_VENDOR_GUIDE_BY_CLAUDE */
  }

  /* -- CUSTOM_PALLETEXCLUDE_BY_CLAUDE (Ryan 2026-09-19) - pallet-fee exclusion picker ----------
     The hidden input `[data-vg-po-field="palletFeeExclusions"]` is the single source of truth for
     what is excluded; every function here reads and writes it, and collectPoFromForm picks it up
     with the rest of the form. The rendered rows are a VIEW of it, which is what lets an excluded
     product the catalog no longer carries still show up (as a "not in catalog" row) instead of
     being silently dropped on the next save. */

  function pfxMoney(c) {
    if (c == null) return '—';
    return '$' + Number(c).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function pfxHiddenEl(wrap) {
    return wrap && wrap.querySelector('[data-vg-po-field="palletFeeExclusions"]');
  }

  function pfxGet(wrap) {
    var h = pfxHiddenEl(wrap);
    return ((h && h.value) || '').split(',')
      .map(function (x) { return x.trim().toLowerCase(); })
      .filter(Boolean);
  }

  function pfxSet(wrap, arr) {
    var h = pfxHiddenEl(wrap);
    if (h) h.value = arr.join(',');
    var label = wrap.querySelector('[data-vg-pfx-count]');
    if (label) {
      label.textContent = arr.length === 0
        ? 'No products excluded'
        : (arr.length === 1 ? '1 product excluded' : arr.length + ' products excluded');
    }
    if (wrap.classList) wrap.classList.toggle('vg-pfx--has', arr.length > 0);
  }

  function pfxRow(handle, title, cost, isOff, orphan) {
    return '<button type="button" class="vg-pfx__item' + (isOff ? ' is-off' : '')
        + (orphan ? ' vg-pfx__item--orphan' : '') + '" data-vg-pfx-item="' + escapeHtml(handle) + '">'
      + '<span class="vg-pfx__mark" aria-hidden="true">' + (isOff ? '&times;' : '') + '</span>'
      + '<span class="vg-pfx__name">' + escapeHtml(title) + '</span>'
      + '<span class="vg-pfx__cost">' + (orphan ? 'not in catalog' : escapeHtml(pfxMoney(cost))) + '</span>'
      + '</button>';
  }

  function pfxRenderList(wrap) {
    if (!wrap) return;
    var listEl = wrap.querySelector('[data-vg-pfx-list]');
    if (!listEl) return;
    var vendor = wrap.getAttribute('data-vendor') || '';
    var entry = state.pfxCache[vendor];
    if (!entry || entry.loading) { listEl.innerHTML = '<p class="vg-pfx__msg">Loading products&hellip;</p>'; return; }
    /* "Could not load" and "nothing here" must not look the same - an empty list that actually
       means a failed request is the one an admin reads as "this vendor has no products". */
    if (entry.error) { listEl.innerHTML = '<p class="vg-pfx__msg vg-pfx__msg--err">' + escapeHtml(entry.error) + '</p>'; return; }
    if (entry.building) { listEl.innerHTML = '<p class="vg-pfx__msg">The product catalog is rebuilding. Reopen this in a few minutes.</p>'; return; }

    var products = entry.products || [];
    var off = pfxGet(wrap);
    var offSet = {};
    off.forEach(function (h) { offSet[h] = true; });
    var searchEl = wrap.querySelector('[data-vg-pfx-search]');
    var q = ((searchEl && searchEl.value) || '').trim().toLowerCase();
    var known = {};
    products.forEach(function (pr) { known[pr.h] = true; });

    /* An excluded handle the catalog no longer carries - discontinued, re-vendored, or retitled
       into a new handle. Rendered FIRST and flagged, so it can be clicked off. Dropping it
       silently would leave a fee exclusion nobody can see and nobody can remove. */
    var rows = off.filter(function (h) { return !known[h] && (!q || h.indexOf(q) !== -1); })
      .map(function (h) { return pfxRow(h, h, null, true, true); });

    rows = rows.concat(products
      .filter(function (pr) { return !q || pr.t.toLowerCase().indexOf(q) !== -1 || pr.h.indexOf(q) !== -1; })
      .map(function (pr) { return pfxRow(pr.h, pr.t, pr.c, !!offSet[pr.h], false); }));

    listEl.innerHTML = rows.length
      ? rows.join('')
      : '<p class="vg-pfx__msg">' + (q ? 'No products match &ldquo;' + escapeHtml(q) + '&rdquo;.' : 'No products found for this vendor.') + '</p>';
  }

  async function pfxLoad(wrap) {
    var vendor = wrap && wrap.getAttribute('data-vendor');
    if (!vendor) return;
    var cached = state.pfxCache[vendor];
    if (cached && !cached.error) return;       // loaded, loading, or building - leave it alone
    state.pfxCache[vendor] = { loading: true };
    pfxRenderList(wrap);
    try {
      var session = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession && window.RepPortal.Auth.getSession();
      var token = session && session.accessToken;
      if (!token) throw new Error('Not authenticated');
      var res = await fetch(RFQ_WORKER_URL + '/vendor-products?vendor=' + encodeURIComponent(vendor), {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      var data = await res.json();
      if (!res.ok) throw new Error((data && data.error) || 'HTTP ' + res.status);
      state.pfxCache[vendor] = { products: data.products || [], building: !!data.building };
    } catch (err) {
      state.pfxCache[vendor] = { error: 'Could not load products: ' + (err.message || String(err)) };
    }
    /* Re-query rather than reusing `wrap`: the modal re-renders on unrelated form activity, so
       the element we started with may no longer be in the document by the time the fetch lands. */
    var live = els.modalBody && els.modalBody.querySelector('[data-vg-pfx]');
    if (live) pfxRenderList(live);
  }

  function pfxHtml(d) {
    var po = d.po || blankPo();
    var vendorName = d.name || '';
    var off = Array.isArray(po.palletFeeExclusions) ? po.palletFeeExclusions : [];
    var countText = off.length === 0
      ? 'No products excluded'
      : (off.length === 1 ? '1 product excluded' : off.length + ' products excluded');
    return '<div class="vg-form__row">' +
      '<label class="vg-form__label">Pallet Fee Exclusions</label>' +
      '<div class="vg-pfx' + (off.length ? ' vg-pfx--has' : '') + '" data-vg-pfx data-vendor="' + escapeHtml(vendorName) + '">' +
        '<button type="button" class="vg-pfx__toggle" data-vg-pfx-toggle aria-expanded="false">' +
          '<span class="vg-pfx__count" data-vg-pfx-count>' + escapeHtml(countText) + '</span>' +
          '<span class="vg-pfx__caret" aria-hidden="true">&#9662;</span>' +
        '</button>' +
        '<div class="vg-pfx__panel" data-vg-pfx-panel hidden>' +
          '<input type="text" class="vg-pfx__search" data-vg-pfx-search autocomplete="off" spellcheck="false" placeholder="Search ' + escapeHtml(vendorName || 'vendor') + ' products&hellip;">' +
          '<div class="vg-pfx__list" data-vg-pfx-list></div>' +
        '</div>' +
        '<input type="hidden" data-vg-po-field="palletFeeExclusions" value="' + escapeHtml(off.join(',')) + '">' +
      '</div>' +
      '<div class="vg-form__hint">Click a product to stop it counting toward the pallet fee; click again to put it back. Excluded items still print on the PO &mdash; they just stop buying a pallet. Sorted by <strong>our cost</strong>, least expensive first.</div>' +
    '</div>';
  }
  /* END CUSTOM_PALLETEXCLUDE_BY_CLAUDE */

  // Renders the admin-only PO Profile subsection inside the vendor edit modal.
  // Fields drive the PO PDF + email flow in the rfq-worker.
  function renderPoSection(d) {
    var po = d.po || blankPo();
    var addr = po.address || {};
    var contact = po.contact || {};
    var emailVal = Array.isArray(contact.email)
      ? contact.email.join(', ')
      : (contact.email || '');
    // CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — stored as a list, edited as a comma string.
    var aliasVal = Array.isArray(po.quoteAliases) ? po.quoteAliases.join(', ') : (po.quoteAliases || '');
    // CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-27) — same list-stored / comma-edited shape.
    var kwVal = Array.isArray(po.aliasKeywords) ? po.aliasKeywords.join(', ') : (po.aliasKeywords || '');
    var ccVal = Array.isArray(contact.cc) ? contact.cc.join(', ') : '';
    var bccVal = Array.isArray(contact.bcc) ? contact.bcc.join(', ') : '';
    function workflowOpt(value, label) {
      var sel = po.workflow === value ? ' selected' : '';
      return '<option value="' + value + '"' + sel + '>' + escapeHtml(label) + '</option>';
    }
    // "Copy this PO data to other vendors..." — for parent-company groupings (e.g.
    // Golden Designs is the source; admin opens it once and pushes its PO data to
    // Maxxus / Dynamic / Dynamic Cold Therapy in one shot). Only shown in edit
    // mode for vendors that have an id (existing record). The list of targets is
    // every OTHER vendor in the Guide.
    var canCopy = d.id && poIsFilled(d.po);
    var targetCheckboxes = canCopy
      ? (state.vendors || [])
        .filter(function (v) { return v && v.id !== d.id; })
        .sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); })
        .map(function (v) {
          var hasPo = poIsFilled(v.po);
          return '<label class="vg-po-copy__option">' +
            '<input type="checkbox" data-vg-po-copy-target="' + escapeHtml(v.id) + '">' +
            '<span class="vg-po-copy__option-name">' + escapeHtml(v.name) + '</span>' +
            (hasPo ? '<span class="vg-po-copy__option-note">already has PO data — will overwrite</span>' : '') +
          '</label>';
        }).join('')
      : '';
    var copyHtml = canCopy ?
      '<div class="vg-po-copy">' +
        '<button type="button" class="vg-po-copy__toggle" data-vg-po-copy-toggle aria-expanded="false">Copy this PO data to other vendors&hellip;</button>' +
        '<div class="vg-po-copy__panel" data-vg-po-copy-panel hidden>' +
          '<p class="vg-po-copy__hint">Select sibling vendors (e.g. parent-company brands) to receive the same PO data. Apply pushes it to vendor-worker immediately.</p>' +
          '<div class="vg-po-copy__list">' + targetCheckboxes + '</div>' +
          '<div class="vg-po-copy__actions">' +
            '<button type="button" class="vg-pill-btn vg-pill-btn--ghost" data-vg-po-copy-cancel>Cancel</button>' +
            '<button type="button" class="vg-pill-btn vg-pill-btn--primary" data-vg-po-copy-apply>Copy to selected &rarr;</button>' +
          '</div>' +
        '</div>' +
      '</div>'
      : (d.id
          ? '<div class="vg-po-copy vg-po-copy--inactive"><p class="vg-po-copy__hint">Fill in PO data above and save once before you can copy it to other vendors.</p></div>'
          : '');
    /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE — Title + dashed top-border removed; the
       tab bar above now provides the section label + visual separation. */
    return '<div class="vg-modal__section">' +
      '<p class="vg-form__hint vg-form__hint--block">Drives Purchase Order generation + the email sent to this vendor. Leave blank for vendors that don\'t get POs (workflow = Request Only).</p>' +
      copyHtml +
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-shortCode">Short Code</label>' +
          '<input class="vg-form__input" id="vg-po-shortCode" data-vg-po-field="shortCode" type="text" maxlength="8" value="' + escapeHtml(po.shortCode || '') + '" />' +
          '<div class="vg-form__hint">3–8 chars. Used in PO numbers like JW-1077-A-{code}.</div>' +
        '</div>' +
        /* CUSTOM_VENDORMENU_BY_CLAUDE (Ryan 2026-08-27) - the label now names the QUESTION the
           field answers, and each option leads with what it DOES rather than what it is called.
           Ryan: *"making it easier to understand how to set up each vendor based on what we're
           trying to achieve."*

           ⛔ "Freight → PO" says HOLD, not "request first". It is a gate: Generate PO stays
           disabled on this vendor's rows until a freight number is recorded or the ask is skipped.
           Ryan set Ferus Prime to it, then asked why the DD was "forcing me to request freight
           before I can send the PO" - the old wording described a sequence and hid the block.

           Values are untouched; this is labels only, so nothing stored has to migrate. */
        '<div>' +
          '<label class="vg-form__label" for="vg-po-workflow">Ordering workflow</label>' +
          '<select class="vg-form__input" id="vg-po-workflow" data-vg-po-field="workflow">' +
            '<option value="">— Choose —</option>' +
            workflowOpt('po-only', 'PO only — send the PO as soon as it is sold') +
            workflowOpt('freight-then-po', 'Freight → PO — hold the PO until freight is known') +
            workflowOpt('web-order', 'Web order — we buy on their site, no PO') +
          '</select>' +
          '<div class="vg-form__hint">What has to happen before we can send this vendor a PO.</div>' +
        '</div>' +
      '</div>' +
      /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — Quote Alias. Sits with Short Code and
         Workflow because it is an identity field, not a shipping one: it says what this supplier
         is CALLED on our paperwork. White-label goods are quoted under our own name, so the vendor
         string on the Shopify line — the string the Deal Detail keys its PO row on — names nobody
         in this Guide, and the PO has nowhere to go. An alias claims that quote name.
         Comma-separated to match the Emails / CC / BCC fields right below it. */
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-quoteAliases">Quote Alias(es) <span class="vg-form__hint vg-form__hint--inline">— separate with commas</span></label>' +
          '<input class="vg-form__input" id="vg-po-quoteAliases" data-vg-po-field="quoteAliases" type="text" value="' + escapeHtml(aliasVal) + '" placeholder="Blue Sky Fitness Supply" />' +
          '<div class="vg-form__hint">Names this vendor goes by on <strong>our</strong> quotes — white-label goods we sell under our own name. A PO for a quote line with that name routes to this vendor. Leave blank unless this vendor white-labels for us.</div>' +
        '</div>' +
        /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — which mailbox this vendor's POs go out
           from. Was a hardcoded vendor list in the dashboard (Legend → Abby) whose own comment said
           it belonged here the moment it outgrew one entry. Also decides what {SENDER_EMAIL} prints
           in the instructions below, so a supplier is never told to reply to a mailbox that did not
           send the PO. Blank keeps today's behaviour: the rep's own saved default decides. */
        '<div>' +
          /* CUSTOM_VENDORFROM_BY_CLAUDE (Ryan 2026-08-27) — RELABELLED from "PO Sends From". The
             field always meant "which of our mailboxes talks to this supplier"; only the PO
             composer had ever consulted it. It now also seeds the freight-quote and weight/dims
             requests, so the old name described one caller rather than the rule.

             ⚠️ The STORED KEY is still `poFrom`, deliberately. Renaming it means migrating 41
             vendor records plus rfq-worker's VENDOR_SEED, plus a read-time coercion for anything
             saved before the migration — all to rename a field nobody sees. The label is the part
             that was lying. Same reasoning as `specialInstructions`, still keyed that way two
             renames later. */
          '<label class="vg-form__label" for="vg-po-poFrom">Emails Send From</label>' +
          '<select class="vg-form__input" id="vg-po-poFrom" data-vg-po-field="poFrom">' +
            '<option value=""' + (!po.poFrom ? ' selected' : '') + '>— Rep default —</option>' +
            '<option value="REP"' + (po.poFrom === 'REP' ? ' selected' : '') + '>Me (ryan@blueskyfitsupply.com)</option>' +
            '<option value="ABBY"' + (po.poFrom === 'ABBY' ? ' selected' : '') + '>Abby (orders@blueskyfitsupply.com)</option>' +
          '</select>' +
          '<div class="vg-form__hint">Sets the From on <strong>every email we send this vendor</strong> — purchase orders, freight-quote requests and weight/dims requests — and the address <code>{SENDER_EMAIL}</code> prints. Still switchable on any individual email.</div>' +
        '</div>' +
      '</div>' +
      /* END CUSTOM_VENDOR_GUIDE_BY_CLAUDE */
      /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-27) - Alias Keywords, directly under the alias
         they disambiguate, because they are meaningless without one. Full-width rather than paired
         into a two-up: this list is long by nature (Ferus Prime's opening set is six phrases) and
         half a row would wrap it into an unreadable ribbon.

         The hint states the uniqueness rule UP FRONT rather than leaving the 409 to teach it. A
         constraint a user only meets by tripping over it reads as a bug in the save button. */
      '<div class="vg-form__row">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-aliasKeywords">Alias Keywords <span class="vg-form__hint vg-form__hint--inline">- separate with commas</span></label>' +
          '<input class="vg-form__input" id="vg-po-aliasKeywords" data-vg-po-field="aliasKeywords" type="text" value="' + escapeHtml(kwVal) + '" placeholder="dumbbell, dumbbells, grip plate, bumper plate, fixed barbell, EZ curl" />' +
          '<div class="vg-form__hint">Only used when <strong>two</strong> vendors claim the same Quote Alias. If a product name on the row contains one of these words, the Deal Detail picks this vendor automatically instead of asking. Matches <strong>whole words in the product name only</strong> - never the sub-text under it - and there is no plural guessing, so list <em>dumbbell</em> and <em>dumbbells</em> separately. <strong>Each keyword can belong to only one vendor</strong>; a repeat is refused on save and names the vendor that already has it.</div>' +
        '</div>' +
      '</div>' +
      /* END CUSTOM_VENDOR_GUIDE_BY_CLAUDE */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Shipping Model + Note. Drives online-order
         Flow detection (parcel-only / freight-in-product / flat-rate = Flow C,
         no freight handshake) and PO PDF rendering of the shipping cell.
         flat-rate also adds a real freight cost line to every PO sent to the
         vendor (the shippingFlatRate field). */
      '<div class="vg-form__row vg-form__row--two">' +
        /* CUSTOM_VENDORMENU_BY_CLAUDE (Ryan 2026-08-27) - renamed from "Shipping Model", which
           named a concept rather than a question, and every option now says who ends up moving the
           goods.

           ⛔ "STANDARD" WAS THE MISLEADING ONE. It reads like "the vendor ships, normally" and
           means close to the opposite: nothing is arranged, and a PO with no recorded freight
           prints "Blue Sky picking up" (po-routes.js resolves bsfsCarrier true when no freight and
           no non-standard model). Every vendor in KV is on this value, so every PO has been taking
           that path - which is correct, and was nowhere on this screen.

           ⚠️ The other three all mean THE SUPPLIER SHIPS. `freight-in-product` in particular sounds
           like "we don't pay freight" and tells the supplier it is already in the price. */
        '<div>' +
          '<label class="vg-form__label" for="vg-po-shippingModel">Freight arrangement</label>' +
          '<select class="vg-form__input" id="vg-po-shippingModel" data-vg-po-field="shippingModel">' +
            '<option value="standard"' + ((po.shippingModel || 'standard') === 'standard' ? ' selected' : '') + '>Standard — we ship it, unless their freight is recorded (default)</option>' +
            '<option value="parcel-only"' + (po.shippingModel === 'parcel-only' ? ' selected' : '') + '>Parcel — they ship, at their own parcel rates</option>' +
            '<option value="freight-in-product"' + (po.shippingModel === 'freight-in-product' ? ' selected' : '') + '>In product cost — they ship, freight already in the price</option>' +
            '<option value="flat-rate"' + (po.shippingModel === 'flat-rate' ? ' selected' : '') + '>Flat rate — they ship, fixed fee per order</option>' +
          '</select>' +
          '<div class="vg-form__hint">Who moves the goods, and what the PO&apos;s freight line says. The last three all mean <strong>the supplier ships</strong>.</div>' +
        '</div>' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-shippingNote">Shipping Note (optional)</label>' +
          '<input class="vg-form__input" id="vg-po-shippingNote" data-vg-po-field="shippingNote" type="text" maxlength="200" value="' + escapeHtml(po.shippingNote || '') + '" />' +
          '<div class="vg-form__hint">Free-text override for the PO shipping cell. Used when Model isn&apos;t Standard.</div>' +
        '</div>' +
      '</div>' +
      '<div class="vg-form__row" data-vg-po-flat-rate-row' + (po.shippingModel === 'flat-rate' ? '' : ' style="display:none"') + '>' +
        /* CUSTOM_PERPRODUCTFREIGHT_BY_CLAUDE (Ryan 2026-10-11) - one fee per order, or their rate per product x quantity
           (the rates list below; rfq-worker lib/per-product-freight.js prints it on the PO). */
        '<div class="vg-form__pair">' +
          '<div>' +
            '<label class="vg-form__label" for="vg-po-flatRateBasis">Charged</label>' +
            '<select class="vg-form__input" id="vg-po-flatRateBasis" data-vg-po-field="flatRateBasis">' +
              '<option value="per-order"' + (po.flatRateBasis !== 'per-product' ? ' selected' : '') + '>Per order - one fee on every PO</option>' +
              '<option value="per-product"' + (po.flatRateBasis === 'per-product' ? ' selected' : '') + '>Per product - their rate for each product x the quantity</option>' +
            '</select>' +
            '<div class="vg-form__hint">Per product prints their rate x quantity on the PO. A product with no rate makes the PO say so, with no amount.</div>' +
          '</div>' +
          '<div data-vg-po-flat-amount>' +
            '<label class="vg-form__label" for="vg-po-shippingFlatRate">Flat Rate Shipping Cost ($ per order)</label>' +
            '<input class="vg-form__input" id="vg-po-shippingFlatRate" data-vg-po-field="shippingFlatRate" type="number" step="0.01" min="0" value="' + escapeHtml(String(po.shippingFlatRate != null ? po.shippingFlatRate : '')) + '" placeholder="0.00" />' +
            '<div class="vg-form__hint">Added as a freight line on every PO sent to this vendor.</div>' +
          '</div>' +
        '</div>' +
      '</div>' +
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_FREIGHTMODE_BY_CLAUDE (ONYX 62ebef416a5f, Ryan 2026-10-03) - who does the freight WORK on
         this supplier's orders, read by the AI agents on every order wake. Advisory: nothing that builds
         a PO or a freight line reads it (Freight arrangement above still does all of that). Blank = the
         agents ask Ryan, except a supplier on Parcel / In product / Flat rate, which reads as "they ship"
         on its own. A locked rate is never guessed: a product with no rate here is asked about. */
      (function () {
        var fm = po.freightMode || '';
        var basis = po.lockedRateBasis || '';
        var ratesText = (Array.isArray(po.lockedRates) ? po.lockedRates : []).map(function (r) {
          var pal = r && r.pallet;
          return r && (r.sku || r.handle) ? ((r.sku || r.handle) + ' = ' + r.rate
            + (pal && pal.length ? '; pallet ' + pal.length + 'x' + pal.width + 'x' + pal.height + ', ' + pal.weightLb + ' lb' : '')) : '';
        }).filter(Boolean).join('\n');
        return '<div class="vg-form__row vg-form__row--two">' +
          '<div>' +
            '<label class="vg-form__label" for="vg-po-freightMode">Freight procedure (for the AI agents)</label>' +
            '<select class="vg-form__input" id="vg-po-freightMode" data-vg-po-field="freightMode">' +
              '<option value=""' + (fm === '' ? ' selected' : '') + '>Not set - agents ask Ryan (default)</option>' +
              '<option value="we-arrange"' + (fm === 'we-arrange' ? ' selected' : '') + '>We arrange - PO first, ready date, GoRocket re-quote, Ryan books</option>' +
              /* CUSTOM_WEARRANGEQUOTE_BY_CLAUDE (ONYX 055fe607fe4d, Ryan 2026-10-08) - Legend's shape: the customer pays freight. */
              '<option value="we-arrange-quote"' + (fm === 'we-arrange-quote' ? ' selected' : '') + '>We arrange, customer pays freight - weights &amp; dims, GoRocket, freight on the quote first</option>' +
              '<option value="locked-rate"' + (fm === 'locked-rate' ? ' selected' : '') + '>Locked rate - compare their rate with GoRocket, pick the better</option>' +
              '<option value="vendor-ships"' + (fm === 'vendor-ships' ? ' selected' : '') + '>They ship - no freight work for us</option>' +
            '</select>' +
            '<div class="vg-form__hint">What the agents do about freight on this supplier\'s orders. Parcel / In product / Flat rate above already read as <strong>they ship</strong> when this is blank. Picking We arrange on one of those is flagged to the agents as a conflict to ask you about; Locked rate is fine, and the agents tell you before a PO goes if our GoRocket rate wins.</div>' +
          '</div>' +
          '<div data-vg-po-locked-row' + (fm === 'locked-rate' ? '' : ' style="display:none"') + '>' +
            '<div data-vg-po-locked-only>' + // CUSTOM_PERPRODUCTFREIGHT_BY_CLAUDE: hidden when only the per-product list applies
            '<label class="vg-form__label" for="vg-po-lockedRateBasis">Locked rate is</label>' +
            '<select class="vg-form__input" id="vg-po-lockedRateBasis" data-vg-po-field="lockedRateBasis">' +
              '<option value=""' + (basis === '' ? ' selected' : '') + '>Not on file yet - agents ask</option>' +
              '<option value="per-order"' + (basis === 'per-order' ? ' selected' : '') + '>One rate per order</option>' +
              '<option value="per-product"' + (basis === 'per-product' ? ' selected' : '') + '>A rate per product</option>' +
            '</select>' +
            '<label class="vg-form__label" for="vg-po-lockedRate" style="margin-top:8px">Rate per order ($)</label>' +
            '<input class="vg-form__input" id="vg-po-lockedRate" data-vg-po-field="lockedRate" type="number" step="0.01" min="0" value="' + escapeHtml(po.lockedRate != null ? String(po.lockedRate) : '') + '" placeholder="blank = not on file" />' +
            '</div>' +
            '<label class="vg-form__label" for="vg-po-lockedRates" style="margin-top:8px" data-vg-po-rates-label>Rates per product (one per line: SKU = 350; pallet 20x78x48, 315 lb)</label>' +
            '<textarea class="vg-form__textarea vg-form__textarea--mono" id="vg-po-lockedRates" data-vg-po-field="lockedRates" rows="4" placeholder="DYN-6119-01 = 350">' + escapeHtml(ratesText) + '</textarea>' +
            '<div class="vg-form__hint">A product not listed is never estimated: the agents ask you for it.</div>' +
          '</div>' +
        '</div>';
      })() +
      /* CUSTOM_FREESHIPONLINE_BY_CLAUDE (ONYX 86f26a61a08d, Ryan 2026-10-04) - we sell this supplier with FREE
         shipping online, so every OFFLINE quote's freight for it is charged FREE ($0) automatically; the
         real freight stays saved as cost and comes out of GP and the commission base. NOT advisory: the
         worker zeroes the charge on apply. Agents cannot tick it (a pricing call). */
      '<div class="vg-form__row">' +
        '<label class="vg-form__checkbox"><input type="checkbox" id="vg-po-freeShippingOnline" data-vg-po-field="freeShippingOnline"' + (po.freeShippingOnline === true ? ' checked' : '') + '> Free shipping online (offline quotes charge freight FREE)</label>' +
        '<div class="vg-form__hint">Ticked: on an unsold offline deal, freight from this supplier goes on the quote as <strong>FREE</strong>. The real freight cost is still saved, comes out of GP, and comes off the GP the commission is paid on (the Freight finalized email shows the rep that cost). A sold deal keeps the charge it was sold at.</div>' +
      '</div>' +
      /* CUSTOM_FREIGHTLEARN_BY_CLAUDE (ONYX a7ae89d49096, Ryan 2026-10-09) - the heaviest pallet this supplier ships. The
         freight estimate never builds a heavier one, and a reading or split that puts more on one pallet is held for a
         person. Blank = the house default (ECORE 2,000 lb, no limit for everyone else). */
      '<div class="vg-form__row">' +
        '<label class="vg-form__label" for="vg-po-maxPalletLb">Heaviest pallet (lb)</label>' +
        '<input class="vg-form__input" id="vg-po-maxPalletLb" data-vg-po-field="maxPalletLb" type="number" step="1" min="100" max="10000" value="' + escapeHtml(po.maxPalletLb != null ? String(po.maxPalletLb) : '') + '" placeholder="blank = house default (ECORE 2,000)" />' +
        '<div class="vg-form__hint">The freight estimate never builds a heavier pallet for this supplier, and a shipment reading that puts more on one pallet is held for a person instead of teaching the data.</div>' +
      '</div>' +
      /* END CUSTOM_FREIGHTMODE_BY_CLAUDE */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — Pallet / Packaging Fee. What this supplier
         charges to palletize and crate a shipment for a truck they are NOT driving, so it applies
         only when Blue Sky brokers the freight — the opposite condition to Flat Rate above, which
         is why it sits in its own row and is always visible rather than hanging off shippingModel.
         Legend Fitness bills $60 per piece of equipment. Blank / 0 = this vendor has no such fee. */
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-palletFee">Pallet / Packaging Fee ($)</label>' +
          '<input class="vg-form__input" id="vg-po-palletFee" data-vg-po-field="palletFee" type="number" step="0.01" min="0" value="' + escapeHtml(String(po.palletFee != null && po.palletFee !== 0 ? po.palletFee : '')) + '" placeholder="0.00" />' +
          '<div class="vg-form__hint">Charged only when <strong>Blue Sky brokers the freight</strong> (our own carrier picks up). Added to the PO and counted as a freight-side cost — it never touches product margin or rep commission. The count is editable per order on the Deal Dashboard.</div>' +
        '</div>' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-palletFeeBasis">Per</label>' +
          '<select class="vg-form__input" id="vg-po-palletFeeBasis" data-vg-po-field="palletFeeBasis">' +
            '<option value="per-product"' + ((po.palletFeeBasis || 'per-product') === 'per-product' ? ' selected' : '') + '>Product — one fee per piece of equipment</option>' +
          '</select>' +
          '<div class="vg-form__hint">Weight-stack upgrades ride their machine and are never counted. Bars and attachments are not detected automatically &mdash; correct those on the order.</div>' +
        '</div>' +
      '</div>' +
      '<div class="vg-form__row">' +
        '<label class="vg-form__label" for="vg-po-palletFeeLabel">Pallet Fee Label (optional)</label>' +
        '<input class="vg-form__input" id="vg-po-palletFeeLabel" data-vg-po-field="palletFeeLabel" type="text" maxlength="80" value="' + escapeHtml(po.palletFeeLabel || '') + '" placeholder="Pallet / Packaging Fee" />' +
        '<div class="vg-form__hint">How the line reads on the PO. Leave blank for &ldquo;Pallet / Packaging Fee&rdquo;.</div>' +
      '</div>' +
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_PALLETEXCLUDE_BY_CLAUDE (Ryan 2026-09-19) - the products this supplier does NOT
         charge the pallet fee on. Ryan: *"I need to create an exclusion list, so certain products
         from said vendors don't get the fee... clicking them simply turns them off, clicking again
         turns them back on."*

         THE SELECTION LIVES IN THE DROPDOWN, NOT BESIDE IT (Ryan, same message: *"don't need to
         keep a long UI list on the pop-up showing what's been selected/de-selected, they just live
         in the drop-down to keep things clean"*). So the closed control is one line - a count - and
         every excluded product is visible only where you clicked it. Resist adding a chip row.

         Cheapest-cost first, because the whole point of the list is the small stuff that rides
         someone else's pallet, and that is what a buyer scans for. The list is fetched from
         rfq-worker's /vendor-products; the hidden input is what actually saves. */
      pfxHtml(d) +
      /* END CUSTOM_PALLETEXCLUDE_BY_CLAUDE */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Organization (PO recipient name).
         Overrides the brand name as the "Vendor" Company on the PO PDF.
         Use for vendors billed/invoiced under a parent-company entity. */
      '<div class="vg-form__row">' +
        '<label class="vg-form__label" for="vg-po-organization">Organization (PO recipient name)</label>' +
        '<input class="vg-form__input" id="vg-po-organization" data-vg-po-field="organization" type="text" maxlength="120" value="' + escapeHtml(po.organization || '') + '" placeholder="Leave blank to use the brand name (' + escapeHtml(d.name || 'vendor') + ')" />' +
        '<div class="vg-form__hint">Prints in place of the brand name in the Vendor column of the PO. Use when the supplier is billed under a parent-company / sister entity.</div>' +
      '</div>' +
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_ALTSOURCE_BY_CLAUDE (Ryan 2026-10-07) - Recycled Surfacing buys for ECORE. */
      '<div class="vg-form__row">' +
        '<label class="vg-form__label" for="vg-po-alternateFor">Buys for (brand)</label>' +
        '<input class="vg-form__input" id="vg-po-alternateFor" data-vg-po-field="alternateFor" type="text" maxlength="80" value="' + escapeHtml(po.alternateFor || '') + '" placeholder="Blank = a normal supplier" />' +
        '<div class="vg-form__hint">Name a brand (e.g. ECORE) when this company is an alternate source for it. That brand\'s lines in the Deal Dashboard get a "Send to ' + escapeHtml(d.name || 'this vendor') + '" option in the + menu, and customers only ever see the brand.</div>' +
      '</div>' +
      /* END CUSTOM_ALTSOURCE_BY_CLAUDE */
      /* CUSTOM_ORDERSTHROUGH_BY_CLAUDE (Ryan 2026-11-02, SHOP-2241) - a brand ordered through its parent company
         (Maxxus Saunas -> Golden Designs): every order puts this brand's products on the parent's PO. Only
         suppliers with a PO short code are offered; the worker re-checks (no chains, no defunct parent). */
      (function () {
        var cur = String(po.ordersThrough || '');
        var opts = (state.vendors || []).filter(function (v) {
          return v && v.id !== d.id && v.name && !v.defunct && v.po && v.po.shortCode && !(v.po.ordersThrough);
        }).sort(function (a, b) { return String(a.name).localeCompare(String(b.name)); });
        var known = !cur || opts.some(function (v) { return v.name === cur; });
        return '<div class="vg-form__row">' +
          '<label class="vg-form__label" for="vg-po-ordersThrough">Order through</label>' +
          '<select class="vg-form__input" id="vg-po-ordersThrough" data-vg-po-field="ordersThrough">' +
            '<option value=""' + (cur ? '' : ' selected') + '>No, this supplier gets its own PO</option>' +
            (known ? '' : '<option value="' + escapeHtml(cur) + '" selected>' + escapeHtml(cur) + '</option>') +
            opts.map(function (v) {
              return '<option value="' + escapeHtml(v.name) + '"' + (v.name === cur ? ' selected' : '') + '>' + escapeHtml(v.name) + ' (' + escapeHtml(v.po.shortCode) + ')</option>';
            }).join('') +
          '</select>' +
          '<div class="vg-form__hint">Pick the parent company when this brand is ordered through it. Its products then go on that supplier\'s purchase order, under that supplier\'s code, together with the parent\'s own products on the same order.</div>' +
        '</div>';
      })() +
      /* END CUSTOM_ORDERSTHROUGH_BY_CLAUDE */
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-street">Street</label>' +
          '<input class="vg-form__input" id="vg-po-street" data-vg-po-field="address.street" type="text" value="' + escapeHtml(addr.street || '') + '" />' +
        '</div>' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-csz">City, State Zip</label>' +
          '<input class="vg-form__input" id="vg-po-csz" data-vg-po-field="address.cityStateZip" type="text" value="' + escapeHtml(addr.cityStateZip || '') + '" />' +
        '</div>' +
      '</div>' +
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-attention">Attention <span class="vg-form__hint vg-form__hint--inline">— printed on the sales order</span></label>' +
          '<input class="vg-form__input" id="vg-po-attention" data-vg-po-field="contact.attention" type="text" value="' + escapeHtml(contact.attention || '') + '" />' +
        '</div>' +
        /* CUSTOM_VENDORGREET_BY_CLAUDE (AIOPS 0c175f74a4f9, 2026-09-08) — the person who processes
           our orders, by first name only, for the greeting on Abby's vendor emails. Kept apart from
           Attention on purpose: that line goes on the printed PO and is usually our reps' names. */
        '<div>' +
          '<label class="vg-form__label" for="vg-po-orders-first-name">Order desk first name <span class="vg-form__hint vg-form__hint--inline">— for "Hi {name},"</span></label>' +
          '<input class="vg-form__input" id="vg-po-orders-first-name" data-vg-po-field="contact.ordersContactFirstName" type="text" maxlength="60" value="' + escapeHtml(contact.ordersContactFirstName || '') + '" placeholder="Carlene" />' +
          /* CUSTOM_VENDORGREET_BY_CLAUDE (AIOPS 81aecf9e517e, Ryan 2026-09-10) — the PO email joined
             the list. It used to open with whatever the template below had typed into it, which is
             how "Hi Carlene," kept going out to Legend months after Carlene left. */
          '<div class="vg-form__hint">Abby\'s PO, freight, dims and follow-up emails open "Hi {name}," when this is set, "Hi Team," when it is blank. Not the Attention line above.</div>' +
        '</div>' +
      '</div>' +
      /* CUSTOM_VENDORGREET_BY_CLAUDE (AIOPS 81aecf9e517e, Ryan 2026-09-10) — THE GREETING KNOB.
         ⛔ Typing a greeting into the email template below no longer does anything: the opening
         line is resolved from this card every time a PO is composed, so a name in a template
         cannot go stale. A greeting that addresses a TEAM ("Hi West Team,") is the one exception
         and is kept as typed — this box is for spelling one out deliberately. */
      '<div class="vg-form__row">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-orders-greeting">Email greeting <span class="vg-form__hint vg-form__hint--inline">— overrides both</span></label>' +
          '<input class="vg-form__input" id="vg-po-orders-greeting" data-vg-po-field="contact.ordersGreeting" type="text" maxlength="80" value="' + escapeHtml(contact.ordersGreeting || '') + '" placeholder="Hi Legend Team," />' +
          '<div class="vg-form__hint">The exact opening line, comma and all. Wins over Order desk first name. Leave blank for "Hi {name}," if a first name is set, otherwise "Hi Team,".</div>' +
        '</div>' +
      '</div>' +
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-phone">Phone</label>' +
          '<input class="vg-form__input" id="vg-po-phone" data-vg-po-field="contact.phone" type="text" value="' + escapeHtml(contact.phone || '') + '" />' +
        '</div>' +
        '<div>' +
          /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — the field has accepted several
             addresses since CC/BCC landed (collectPoFromForm splits on commas, the worker stores
             a list, po-routes.js sends to all of them). It read as single-recipient only because
             the label said "Email" while the one below it said "CC Emails". Say so in the label
             and the placeholder; nothing about the behaviour changed. */
          '<label class="vg-form__label" for="vg-po-email">Emails <span class="vg-form__hint vg-form__hint--inline">— separate with commas</span></label>' +
          '<input class="vg-form__input" id="vg-po-email" data-vg-po-field="contact.email" type="text" value="' + escapeHtml(emailVal) + '" placeholder="orders@vendor.com, buyer@vendor.com" />' +
        '</div>' +
      '</div>' +
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-09-06, Spirit Fitness) — these two are the
             PURCHASE ORDER's copies. The freight and weight & dims asks have their own To/CC/BCC
             below and no longer inherit this list (Nick was copied on a rate request). */
          '<label class="vg-form__label" for="vg-po-cc">CC Emails <span class="vg-form__hint vg-form__hint--inline">— purchase orders only; freight and dims asks use their own CC below</span></label>' +
          '<input class="vg-form__input" id="vg-po-cc" data-vg-po-field="contact.cc" type="text" value="' + escapeHtml(ccVal) + '" />' +
        '</div>' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-bcc">BCC Emails <span class="vg-form__hint vg-form__hint--inline">— purchase orders only</span></label>' +
          '<input class="vg-form__input" id="vg-po-bcc" data-vg-po-field="contact.bcc" type="text" value="' + escapeHtml(bccVal) + '" />' +
        '</div>' +
      '</div>' +
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-terms">Payment Terms</label>' +
          '<input class="vg-form__input" id="vg-po-terms" data-vg-po-field="paymentTerms" type="text" value="' + escapeHtml(po.paymentTerms || '') + '" />' +
        '</div>' +
        /* CUSTOM_POSTPOWAIT_BY_CLAUDE (AIOPS 82b958ec5f02, Ryan 2026-09-12) — WHAT WE WAIT FOR
           AFTER THE PO GOES.
           ⛔ IT SITS BESIDE Payment Terms BECAUSE THAT IS THE FIELD IT IS OFTEN CONFUSED WITH, and
           the pair reads as the distinction it is: "Pay in full" is the deal we have with them, a
           sentence for a person; this is the one machine-readable fact — are we now blocked, and
           on what. ANCORE's body has asked Rachel for a payment link for months and nothing could
           act on it, because a request inside an email template is not a board state.
           ⚠️ DEFAULT "Nothing" FOR EVERY EXISTING VENDOR. Defaulting to a wait would open a chase
           against the whole Guide the day this deploys, which is how a new signal gets ignored. */
        '<div>' +
          '<label class="vg-form__label" for="vg-po-postpowait">After the PO is sent, we wait for</label>' +
          '<select class="vg-form__input" id="vg-po-postpowait" data-vg-po-field="postPoWait">' +
            '<option value="none"' + ((po.postPoWait || 'none') === 'none' ? ' selected' : '') + '>Nothing — the PO is the end of it (default)</option>' +
            '<option value="ack"' + (po.postPoWait === 'ack' ? ' selected' : '') + '>An acknowledgement — confirmation they received it</option>' +
            '<option value="payment-link"' + (po.postPoWait === 'payment-link' ? ' selected' : '') + '>A payment link — they invoice us before they build or ship</option>' +
          '</select>' +
          '<span class="vg-form__hint">Only "a payment link" is tracked: the deal reads "Waiting on vendor payment link" until it lands, and the supplier shows as overdue after two working days. Nothing is chased automatically and nothing is paid.</span>' +
        '</div>' +
      '</div>' +
      /* CUSTOM_VENDORFOLLOWUPDAYS_BY_CLAUDE (ONYX bf6a05875ee5, Ryan 2026-10-16) - how long between the reminders the worker
         sends this supplier. One "day" is a supplier business day: 6 AM to 6 PM Mountain, seven days a week (3 days = 36 hours).
         Decimals allowed. Blank = 3. */
      '<div class="vg-form__row">' +
        '<label class="vg-form__label" for="vg-po-poFollowupDays">Days between PO follow-ups</label>' +
        '<input class="vg-form__input" id="vg-po-poFollowupDays" data-vg-po-field="poFollowupDays" type="number" step="0.1" min="0.1" max="30" value="' + escapeHtml(po.poFollowupDays != null ? String(po.poFollowupDays) : '') + '" placeholder="blank = 3" />' +
        '<div class="vg-form__hint">How long after the PO, and after each reminder, before the next reminder asking them to confirm it. A day is 6 AM to 6 PM Mountain, every day of the week, so 3 days is 36 business hours. Decimals are fine (1.6). Each reminder is a [TEST] to Ryan first.</div>' +
      '</div>' +
      '<div class="vg-form__row">' +
        '<label class="vg-form__label" for="vg-po-etaFollowupDays">Days between ETA follow-ups</label>' +
        '<input class="vg-form__input" id="vg-po-etaFollowupDays" data-vg-po-field="etaFollowupDays" type="number" step="0.1" min="0.1" max="30" value="' + escapeHtml(po.etaFollowupDays != null ? String(po.etaFollowupDays) : '') + '" placeholder="blank = 3" />' +
        '<div class="vg-form__hint">Once they have confirmed the PO, how long before we ask for the ready date, and between ready-date reminders. Same business-day clock.</div>' +
      '</div>' +
      /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Ryan 2026-10-11, Product Rules Session 8) - HOW WE PAY, AND THEIR DEALER PORTAL.
         Part of the Product Rules brand checklist ("Vendor Guide complete" = an agent can order, pay and ship
         without asking Ryan). ⛔ The portal login is the NAME of the login in the portal credential store
         (gorocket, ecore, powerblock...), never a username or password (Ryan: "Please never put a username or
         password in the Guide"); the worker keeps only a short name there. */
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-paymethod">How we pay them</label>' +
          '<select class="vg-form__input" id="vg-po-paymethod" data-vg-po-field="payMethod">' +
            [['', 'Not set yet'], ['card', 'Card'], ['ach', 'ACH / bank transfer'], ['check', 'Check'], ['wire', 'Wire'],
              ['payment-link', 'Their payment link'], ['their-portal', 'Through their portal'], ['net-terms', 'Net terms (they invoice, we pay later)']]
              .map(function (o) { return '<option value="' + o[0] + '"' + ((po.payMethod || '') === o[0] ? ' selected' : '') + '>' + o[1] + '</option>'; }).join('') +
          '</select>' +
        '</div>' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-portalurl">Dealer portal address</label>' +
          '<input class="vg-form__input" id="vg-po-portalurl" data-vg-po-field="dealerPortalUrl" type="url" placeholder="https://..." value="' + escapeHtml(po.dealerPortalUrl || '') + '" />' +
        '</div>' +
      '</div>' +
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-portalaccess">How we get into their portal</label>' +
          '<input class="vg-form__input" id="vg-po-portalaccess" data-vg-po-field="dealerPortalAccess" type="text" placeholder="e.g. Ryan\'s dealer login; agents sign in with the stored login" value="' + escapeHtml(po.dealerPortalAccess || '') + '" />' +
        '</div>' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-portallogin">Stored login name</label>' +
          '<input class="vg-form__input" id="vg-po-portallogin" data-vg-po-field="dealerPortalLogin" type="text" placeholder="e.g. ecore" value="' + escapeHtml(po.dealerPortalLogin || '') + '" />' +
          '<span class="vg-form__hint">The name of the login in the portal credential store (gorocket, ecore, powerblock). Never type a username or password here.</span>' +
        '</div>' +
      '</div>' +
      /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Ryan 2026-10-04, Product Rules Session 2) - THE FOLD-IN. Three
         supplier facts that lived only in portal lists keyed by name now live HERE (Ryan's pick).
         "Not set" leaves the old portal list in charge for this supplier; Yes or No decides outright.
         Product Rules edits the same three fields. */
      (function () {
        function tri(key, label, opts, hint) {
          var cur = po[key] == null ? '' : String(po[key]);
          return '<div>' +
            '<label class="vg-form__label" for="vg-po-' + key + '">' + escapeHtml(label) + '</label>' +
            '<select class="vg-form__input" id="vg-po-' + key + '" data-vg-po-field="' + key + '">' +
              opts.map(function (o) { return '<option value="' + escapeHtml(o[0]) + '"' + (cur === o[0] ? ' selected' : '') + '>' + escapeHtml(o[1]) + '</option>'; }).join('') +
            '</select>' +
            '<span class="vg-form__hint">' + escapeHtml(hint) + '</span>' +
          '</div>';
        }
        return '<div class="vg-form__row vg-form__row--three">' +
          tri('settleMode', 'Card charged when the PO is sent', [['', 'Not set (portal list decides)'], ['vendor_charges_cc_on_file', 'Yes - they charge our card on file'], ['manual', 'No - a person settles the bill']],
            'Yes closes the payable on PO send (it moves no money).') +
          tri('freightBilling', 'Supplier bills us the freight', [['', 'Not set (portal list decides)'], ['vendor_bills_us', 'Yes - on their invoice'], ['broker', 'No - we arrange the freight']],
            'Yes puts the freight cost on their PO. It never changes what the customer is charged.') +
          tri('costsAreEstimates', 'Their costs are estimates', [['', 'Not set (portal list decides)'], ['true', 'Yes - true up from their quote'], ['false', 'No - the price list is the cost']],
            'Yes lets an agent record the true unit cost off their quote.') +
        '</div>';
      })() +
      '<div class="vg-form__row vg-form__row--two">' +
        '<div>' +
          '<label class="vg-form__label" for="vg-po-subj">Email Subject</label>' +
          '<input class="vg-form__input" id="vg-po-subj" data-vg-po-field="emailSubject" type="text" value="' + escapeHtml(po.emailSubject || '') + '" placeholder="Blue Sky Order | {ZIP} | {PO}" />' +
        '</div>' +
        '<div></div>' +
      '</div>' +
      // Two default-instruction editors — one per shipping mode. Backward compat:
      // if the legacy specialInstructions is set but defaultInstructionsVendor is
      // empty, pre-fill vendor mode with the legacy value (the more common case).
      (function () {
        var legacyMigrate = !po.defaultInstructionsVendor && po.specialInstructions ? po.specialInstructions : '';
        var vendorVal = po.defaultInstructionsVendor || legacyMigrate;
        var bsfsVal = po.defaultInstructionsBsfs || '';
        function buildEditor(mode, value, hiddenField, hint, placeholder) {
          var lines = (value || '').split('\n').map(function (s) { return s.trim(); }).filter(Boolean);
          var items = lines.map(function (line) {
            return '<li class="vg-po-instr-item"><span class="vg-po-instr-text">' + escapeHtml(line) + '</span><button type="button" class="vg-po-instr-x" data-vg-po-instr-remove aria-label="Remove">&times;</button></li>';
          }).join('');
          return '<div class="vg-form__row">' +
            '<label class="vg-form__label">' + escapeHtml(hint.label) + '</label>' +
            '<div class="vg-po-instr-editor" data-vg-po-instr-editor data-mode="' + mode + '">' +
              '<ul class="vg-po-instr-list">' + items + '</ul>' +
              '<div class="vg-po-instr-add">' +
                '<input type="text" class="vg-form__input" data-vg-po-instr-input placeholder="' + escapeHtml(placeholder) + '" maxlength="120">' +
                '<button type="button" class="vg-pill-btn" data-vg-po-instr-add>Add</button>' +
              '</div>' +
            '</div>' +
            '<input type="hidden" data-vg-po-field="' + hiddenField + '" value="' + escapeHtml(value || '') + '">' +
            '<div class="vg-form__hint">' + escapeHtml(hint.help) + '</div>' +
          '</div>';
        }
        /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — {SENDER_EMAIL} documented where the
           lines are actually typed, and the placeholder no longer teaches a hardcoded address:
           every vendor that had one is exactly why this token exists. */
        var _tokenHelp = ' Type {SENDER_EMAIL} for the mailbox the PO is sent from (Me or Abby) — it resolves on the PDF.';
        return buildEditor('vendor', vendorVal, 'defaultInstructionsVendor',
          { label: 'Default Instructions — Vendor Ships', help: 'Used when this vendor handles shipping (their carrier, freight on PO).' + _tokenHelp },
          'e.g., Please send tracking to {SENDER_EMAIL}')
        + buildEditor('bsfs', bsfsVal, 'defaultInstructionsBsfs',
          { label: 'Default Instructions — Blue Sky Carrier', help: 'Used when Blue Sky uses our own carrier (we send the BOL).' + _tokenHelp },
          'e.g., Please include call-ahead notification');
      })() +
      // Two PO email bodies — one per shipping mode (mirrors the two default-
      // instruction editors). po-routes.js picks emailBodyBsfs when Blue Sky
      // ships on our own carrier, emailBodyVendor when the vendor ships, with a
      // cross-mode + legacy emailBody fallback. Backward compat: if the legacy
      // single emailBody is set but emailBodyVendor is empty, pre-fill the vendor
      // slot with it (the common case) so existing copy isn't lost.
      (function () {
        var legacyBody = po.emailBody || '';
        var bodyVendorVal = po.emailBodyVendor || legacyBody;
        var bodyBsfsVal = po.emailBodyBsfs || '';
        /* CUSTOM_POEMAIL_BY_CLAUDE (Ryan 2026-09-05) — the placeholder IS the house default the
           worker uses for a blank field (rfq-worker lib/po-email-template.js PO_EMAIL_DEFAULT_BODY),
           so what an admin sees greyed-out is what actually goes out. Keep the two in step. */
        var _ph = 'Hi Team,&#10;&#10;Please see the attached PO {PO}.&#10;&#10;Kindly confirm receipt and process this order at your earliest convenience.&#10;&#10;**Thank you!**';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — this hint used to advertise
           {ZIP} in the BODY, where po-routes.js never substituted it, so it shipped to
           the vendor as literal text. Subject and body now share one token vocabulary
           (po-routes.js resolves all of them in both), and {PO} is documented. */
        var _tokenHint = 'Tokens: <code>{PO}</code>, <code>{REF}</code>, <code>{ZIP}</code>, <code>{ITEMS_BLOCK}</code>, <code>{SHIP_TO_BLOCK}</code>.';
        function buildBodyEditor(id, field, labelText, helpText, value) {
          return '<div class="vg-form__row">' +
            '<label class="vg-form__label" for="' + id + '">' + escapeHtml(labelText) + '</label>' +
            '<textarea class="vg-form__textarea vg-form__textarea--mono" id="' + id + '" data-vg-po-field="' + field + '" rows="6" placeholder="' + _ph + '">' + escapeHtml(value) + '</textarea>' +
            '<div class="vg-form__hint">' + helpText + ' ' + _tokenHint + '</div>' +
          '</div>';
        }
        return buildBodyEditor('vg-po-body-vendor', 'emailBodyVendor', 'PO Email Body — Vendor Ships',
          'Used when this vendor handles shipping (their carrier, freight on PO).', bodyVendorVal)
          + buildBodyEditor('vg-po-body-bsfs', 'emailBodyBsfs', 'PO Email Body — Blue Sky Carrier',
            'Used when Blue Sky ships on our own carrier (we send the BOL).', bodyBsfsVal);
      })() +
      // CUSTOM_RFQPORTAL_BY_CLAUDE — Freight-request email verbiage. Only applies to
      // the Freight → PO workflow (the fieldset is disabled/grayed for other workflows).
      // Two asks: weight & dimensions, and freight/pricing. Blank = built-in default
      // copy. The body is the FULL message (incl. greeting); the items table, SHIP TO,
      // and signature are appended automatically. Tokens {VENDOR}/{ZIP}/{REF} supported.
      (function () {
        var isFreightWf = po.workflow === 'freight-then-po';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — {ADD_ONS_BLOCK} documented,
           and the placeholders now show the gold-standard copy so a blank field and a
           copied-out template produce the same email. A dims ask carries no SHIP TO or
           ADD ONS block by design — the supplier only weighs and measures. */
        var _dimsTok = 'Body tokens: <code>{VENDOR}</code>, <code>{ZIP}</code>, <code>{REF}</code>, <code>{ITEMS_BLOCK}</code>. Leave body blank for the standard wording; ITEMS appends automatically when not placed inline.';
        var _quoteTok = 'Body tokens: <code>{VENDOR}</code>, <code>{ZIP}</code>, <code>{REF}</code>, <code>{SHIP_TO_BLOCK}</code>, <code>{ADD_ONS_BLOCK}</code>, <code>{ITEMS_BLOCK}</code>. Leave body blank for the standard wording; a block with nothing to show (no add-ons active) disappears cleanly.';
        var _dimsPh = 'Hi Team,&#10;&#10;Please send the packaged weight and dimensions for the items listed below. **Thank you!**&#10;&#10;{ITEMS_BLOCK}';
        var _quotePh = 'Hi Team,&#10;&#10;Please send a freight quote for the items listed below. **Thank you!**&#10;&#10;{SHIP_TO_BLOCK}&#10;&#10;{ADD_ONS_BLOCK}&#10;&#10;{ITEMS_BLOCK}';
        var _recipHint = 'Comma-separated. Overrides the vendor contact on file. Leave blank to use the Vendor Guide contact.';
        return '<fieldset class="vg-po-freight" data-vg-po-freight-block' + (isFreightWf ? '' : ' disabled') + '>' +
          '<div class="vg-po-freight__head">Freight-Request Emails' +
            '<span class="vg-po-freight__na" data-vg-po-freight-na' + (isFreightWf ? ' style="display:none"' : '') + '>Freight → PO workflow only</span>' +
          '</div>' +
          // ── Weight & Dimensions ──
          '<p class="vg-form__hint vg-form__hint--block" style="margin-top:8px;"><strong>Weight &amp; Dimensions</strong></p>' +
          '<div class="vg-form__row">' +
            '<label class="vg-form__label" for="vg-po-freight-dims-subj">Subject</label>' +
            '<input class="vg-form__input" id="vg-po-freight-dims-subj" data-vg-po-field="freightDimsSubject" type="text" maxlength="200" value="' + escapeHtml(po.freightDimsSubject || '') + '" placeholder="{VENDOR} — Weight &amp; Dimensions Request | {ZIP}" />' +
          '</div>' +
          '<div class="vg-form__row">' +
            '<label class="vg-form__label" for="vg-po-freight-dims-body">Body</label>' +
            '<textarea class="vg-form__textarea vg-form__textarea--mono" id="vg-po-freight-dims-body" data-vg-po-field="freightDimsBody" rows="5" placeholder="' + _dimsPh + '">' + escapeHtml(po.freightDimsBody || '') + '</textarea>' +
            '<div class="vg-form__hint">' + _dimsTok + '</div>' +
          '</div>' +
          '<div class="vg-form__row">' +
            '<label class="vg-form__label" for="vg-po-dims-to">To (override)</label>' +
            '<input class="vg-form__input" id="vg-po-dims-to" data-vg-po-field="freightDimsTo" type="text" value="' + escapeHtml(po.freightDimsTo || '') + '" placeholder="vendor@example.com" />' +
            '<div class="vg-form__hint">' + _recipHint + '</div>' +
          '</div>' +
          '<div class="vg-form__row vg-form__row--two">' +
            '<div>' +
              '<label class="vg-form__label" for="vg-po-dims-cc">CC</label>' +
              '<input class="vg-form__input" id="vg-po-dims-cc" data-vg-po-field="freightDimsCc" type="text" value="' + escapeHtml(po.freightDimsCc || '') + '" placeholder="cc@example.com" />' +
            '</div>' +
            '<div>' +
              '<label class="vg-form__label" for="vg-po-dims-bcc">BCC</label>' +
              '<input class="vg-form__input" id="vg-po-dims-bcc" data-vg-po-field="freightDimsBcc" type="text" value="' + escapeHtml(po.freightDimsBcc || '') + '" placeholder="bcc@example.com" />' +
            '</div>' +
          '</div>' +
          // ── Freight / Pricing ──
          '<p class="vg-form__hint vg-form__hint--block" style="margin-top:16px;"><strong>Freight / Pricing</strong></p>' +
          '<div class="vg-form__row">' +
            '<label class="vg-form__label" for="vg-po-freight-quote-subj">Subject</label>' +
            '<input class="vg-form__input" id="vg-po-freight-quote-subj" data-vg-po-field="freightQuoteSubject" type="text" maxlength="200" value="' + escapeHtml(po.freightQuoteSubject || '') + '" placeholder="{VENDOR} — Freight Quote Request | {ZIP}" />' +
          '</div>' +
          '<div class="vg-form__row">' +
            '<label class="vg-form__label" for="vg-po-freight-quote-body">Body</label>' +
            '<textarea class="vg-form__textarea vg-form__textarea--mono" id="vg-po-freight-quote-body" data-vg-po-field="freightQuoteBody" rows="5" placeholder="' + _quotePh + '">' + escapeHtml(po.freightQuoteBody || '') + '</textarea>' +
            '<div class="vg-form__hint">' + _quoteTok + '</div>' +
          '</div>' +
          '<div class="vg-form__row">' +
            '<label class="vg-form__label" for="vg-po-quote-to">To (override)</label>' +
            '<input class="vg-form__input" id="vg-po-quote-to" data-vg-po-field="freightQuoteTo" type="text" value="' + escapeHtml(po.freightQuoteTo || '') + '" placeholder="vendor@example.com" />' +
            '<div class="vg-form__hint">' + _recipHint + '</div>' +
          '</div>' +
          '<div class="vg-form__row vg-form__row--two">' +
            '<div>' +
              '<label class="vg-form__label" for="vg-po-quote-cc">CC</label>' +
              '<input class="vg-form__input" id="vg-po-quote-cc" data-vg-po-field="freightQuoteCc" type="text" value="' + escapeHtml(po.freightQuoteCc || '') + '" placeholder="cc@example.com" />' +
            '</div>' +
            '<div>' +
              '<label class="vg-form__label" for="vg-po-quote-bcc">BCC</label>' +
              '<input class="vg-form__input" id="vg-po-quote-bcc" data-vg-po-field="freightQuoteBcc" type="text" value="' + escapeHtml(po.freightQuoteBcc || '') + '" placeholder="bcc@example.com" />' +
            '</div>' +
          '</div>' +
        '</fieldset>';
      })() +
    '</div>';
  }

  // Read PO fields from the form, support nested-key naming (address.street, contact.cc).
  function collectPoFromForm(target, root) {
    var po = target.po || blankPo();
    qsa('[data-vg-po-field]', root).forEach(function (el) {
      var key = el.getAttribute('data-vg-po-field');
      /* CUSTOM_FREESHIPONLINE_BY_CLAUDE - a checkbox posts its tick, never the string "on". */
      if (el.type === 'checkbox') { po[key] = !!el.checked; return; }
      var value = el.value;
      var path = key.split('.');
      if (path.length === 1) {
        // Special-case cc list (comma-separated)
        if (key === 'cc') {
          po.cc = value.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-26) — Quote Alias is a list on the record
           and a comma string in the form, same as cc/bcc/email. Always send the array, even when
           empty: the worker treats a present-but-empty list as "clear it", which is the only way
           to retire an alias — and a stale alias mis-routes a PO. */
        } else if (key === 'quoteAliases') {
          po.quoteAliases = value.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-27) — Alias Keywords, same contract: always
           send the array so an emptied box genuinely clears the list. A keyword left behind here
           would keep steering POs at a supplier the admin just stopped associating with it. */
        } else if (key === 'aliasKeywords') {
          po.aliasKeywords = value.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-09-19) — pallet-fee exclusions: a comma string
           in the hidden input, a list on the record. ALWAYS sent, even when empty — both workers
           read a present-but-empty array as "clear it", and that is the only way the last
           exclusion ever comes off. */
        } else if (key === 'palletFeeExclusions') {
          po.palletFeeExclusions = value.split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
        /* CUSTOM_FREIGHTMODE_BY_CLAUDE - "handle = 450" lines -> [{handle, rate}]. ALWAYS sent, so an emptied
           box clears the list (present-but-empty = clear, the palletFeeExclusions contract). */
        } else if (key === 'lockedRates') {
          po.lockedRates = value.split(/\r?\n/).map(function (line) {
            /* (Ryan 2026-10-04) an optional "; pallet LxWxH, N lb" - the one pallet the product ships on. */
            var m = line.match(/^\s*([^=:\s]+)\s*[=:]\s*\$?\s*([0-9]+(?:\.[0-9]+)?)\s*(?:[;|]\s*pallet\s+([0-9.]+)\s*x\s*([0-9.]+)\s*x\s*([0-9.]+)\s*(?:in)?\s*,?\s*([0-9.]+)\s*lbs?\s*)?$/i);
            /* (Ryan 2026-10-04) an id with a capital letter is the supplier's SKU (DYN-6119-01), kept as typed; all lower case is a Shopify handle. */
            if (!m) return null;
            var row = /[A-Z]/.test(m[1]) ? { sku: m[1], rate: parseFloat(m[2]) } : { handle: m[1], rate: parseFloat(m[2]) };
            if (m[3]) row.pallet = { length: parseFloat(m[3]), width: parseFloat(m[4]), height: parseFloat(m[5]), weightLb: parseFloat(m[6]) };
            return row;
          }).filter(Boolean);
        /* END CUSTOM_VENDOR_GUIDE_BY_CLAUDE */
        } else {
          po[key] = value;
        }
      } else if (path.length === 2) {
        po[path[0]] = po[path[0]] || {};
        if (path[0] === 'contact' && (path[1] === 'cc' || path[1] === 'bcc' || path[1] === 'email')) {
          po.contact[path[1]] = value.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        } else {
          po[path[0]][path[1]] = value;
        }
      }
    });
    if (po.shortCode) po.shortCode = String(po.shortCode).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
    target.po = po;
  }

  function renderRecipientsSection() {
    var body;
    if (state.recipientsLoading && !state.recipients) {
      body = '<p class="vg-empty__text">Loading…</p>';
    } else if (!state.recipients) {
      body = '<p class="vg-empty__text">Recipients unavailable.</p>';
    } else {
      var codes = Object.keys(state.recipients).sort();
      if (!codes.length) {
        body = '<p class="vg-empty__text">No reps found in directory.</p>';
      } else {
        body = codes.map(function (code) {
          var r = state.recipients[code];
          var name = r.name || code;
          var email = r.email || '';
          var optin = r.optin !== false;
          return (
            '<div class="vg-recipient-row">' +
              '<div class="vg-recipient-row__info">' +
                '<span class="vg-recipient-row__name">' + escapeHtml(name) + '</span>' +
                '<span class="vg-recipient-row__code">' + escapeHtml(code) + '</span>' +
                (email ? '<span class="vg-recipient-row__email">' + escapeHtml(email) + '</span>' : '<span class="vg-recipient-row__email vg-recipient-row__email--missing">no email</span>') +
              '</div>' +
              '<label class="vg-switch" title="' + (optin ? 'Notifications on' : 'Notifications off') + '">' +
                '<input type="checkbox" data-vg-recipient-toggle data-code="' + escapeHtml(code) + '"' + (optin ? ' checked' : '') + (email ? '' : ' disabled') + '>' +
                '<span class="vg-switch__track"></span>' +
              '</label>' +
            '</div>'
          );
        }).join('');
      }
    }

    return (
      '<div class="vg-recipient-list" data-vg-recipients>' + body + '</div>' +
      '<div class="vg-recipients-panel__foot">' +
        '<button type="button" class="vg-recipient-test-btn" data-vg-recipient-test>Send a sample email to me</button>' +
      '</div>'
    );
  }

  /* CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11) - the list lives in the admin bar's "Upload emails" panel. */
  function refreshRecipientsSection() {
    if (els.recipientsBody) els.recipientsBody.innerHTML = renderRecipientsSection();
  }

  function toggleRecipientsPanel(btn) {
    if (!els.recipientsPanel) return;
    var open = !els.recipientsPanel.hidden;
    if (open) { vgSlide(els.recipientsPanel, false); btn.setAttribute('aria-expanded', 'false'); return; }
    refreshRecipientsSection();
    vgSlide(els.recipientsPanel, true);
    btn.setAttribute('aria-expanded', 'true');
    fetchRecipients(true);
  }

  async function fetchRecipients(force) {
    if (state.recipientsLoading) return;
    if (state.recipients && !force) return;
    state.recipientsLoading = true;
    try {
      var data = await apiFetch('/notifications/optin');
      state.recipients = (data && data.recipients) || {};
    } catch (err) {
      state.recipients = null;
      console.warn('Vendor Guide: fetchRecipients failed', err);
    } finally {
      state.recipientsLoading = false;
      refreshRecipientsSection();
    }
  }

  async function toggleRecipientOptIn(code, optin, inputEl) {
    if (!state.recipients || !state.recipients[code]) return;
    var previous = state.recipients[code].optin !== false;
    // Optimistic update
    state.recipients[code].optin = optin;
    // Build full map from current state.
    var map = {};
    Object.keys(state.recipients).forEach(function (k) {
      map[k] = state.recipients[k].optin !== false;
    });
    try {
      await apiFetch('/notifications/optin', {
        method: 'PUT',
        body: JSON.stringify(map)
      });
    } catch (err) {
      // Revert on failure
      state.recipients[code].optin = previous;
      if (inputEl) inputEl.checked = previous;
      showToast('Failed to update: ' + errMsg(err), 'error');
    }
  }

  async function sendTestNotification() {
    // The sample names a supplier; any one will do (the first in the Guide).
    var first = (state.vendors || []).filter(function (v) { return v && !v.defunct; })
      .sort(function (a, b) { return String(a.name).localeCompare(String(b.name)); })[0];
    var id = first && first.id;
    if (!id) { showToast('No supplier to put in the sample yet.', 'error'); return; }
    var btn = qs('[data-vg-recipient-test]');
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    try {
      var data = await apiFetch('/vendor/' + encodeURIComponent(id) + '/test-notification', {
        method: 'POST'
      });
      if (btn) btn.textContent = 'Sent ✓';
      setTimeout(function () {
        var live = qs('[data-vg-recipient-test]');
        if (live) { live.disabled = false; live.textContent = 'Send a sample email to me'; }
      }, 2500);
      if (data && data.sentTo) {
        console.log('Vendor Guide: test notification sent to', data.sentTo);
      }
    } catch (err) {
      if (btn) { btn.disabled = false; btn.textContent = 'Send a sample email to me'; }
      showToast('Test failed: ' + errMsg(err), 'error');
    }
  }

  function renderPlacementsEditor(draft) {
    if (!draft._ui) initPlacementsUi(draft);
    var ui = draft._ui;

    var brandTierChecks = ['good', 'better', 'best'].map(function (t) {
      var checked = (draft.tiers || []).indexOf(t) !== -1 ? ' checked' : '';
      return '<label><input type="checkbox" data-vg-tier-checkbox="' + t + '"' + checked + '><span>' + TIER_LABELS[t] + '</span></label>';
    }).join('');
    var brandMarginChecks = [1,2,3,4].map(function (m) {
      var checked = (draft.margins || []).indexOf(m) !== -1 ? ' checked' : '';
      return '<label><input type="checkbox" data-vg-margin-checkbox="' + m + '"' + checked + '><span>' + MARGIN_LABELS[m] + '</span></label>';
    }).join('');

    var defaultsBlock =
      '<div class="vg-ple__defaults">' +
        '<p class="vg-ple__defaults-label">Brand-level tier &amp; margin — defaults for every placement. Override individual sub-categories below.</p>' +
        '<div class="vg-ple__defaults-row"><span class="vg-ple__defaults-name">Tier</span><div class="vg-placement-row__radios">' + brandTierChecks + '</div></div>' +
        '<div class="vg-ple__defaults-row"><span class="vg-ple__defaults-name">Margin</span><div class="vg-placement-row__radios">' + brandMarginChecks + '</div></div>' +
      '</div>';

    if (!state.taxonomy) return defaultsBlock + '<p class="vg-empty__text">Loading sub-categories…</p>';

    var categoriesHtml = state.taxonomy.categories.map(function (cat) {
      var subs = cat.subcategories || [];
      var items = subs.map(function (sub) {
        return renderPlacementItem(sub, ui);
      }).join('');
      var selectedCount = subs.filter(function (sub) { return !!ui.selected[sub.key]; }).length;
      var countClass = selectedCount > 0 ? ' vg-ple__cat-count--active' : '';
      var allChecked = selectedCount === subs.length;
      return (
        '<details class="vg-ple__cat">' +
          '<summary class="vg-ple__cat-title">' +
            '<span class="vg-ple__cat-chevron" aria-hidden="true"></span>' +
            '<label class="vg-ple__cat-select-all" onclick="event.stopPropagation()">' +
              '<input type="checkbox" data-vg-pl-select-all="' + escapeHtml(cat.key) + '"' + (allChecked ? ' checked' : '') + '>' +
            '</label>' +
            '<span class="vg-ple__cat-name">' + escapeHtml(cat.name) + '</span>' +
            '<span class="vg-ple__cat-count' + countClass + '" data-vg-ple-count>' + selectedCount + ' / ' + subs.length + '</span>' +
          '</summary>' +
          '<div class="vg-ple__items">' + items + '</div>' +
        '</details>'
      );
    }).join('');

    return defaultsBlock + '<div class="vg-ple__cats">' + categoriesHtml + '</div>';
  }

  function renderPlacementItem(sub, ui) {
    var isSelected = !!ui.selected[sub.key];
    var ov = ui.overrides[sub.key] || {};
    var ovTiers = ov.tiers || [];
    var ovMargins = ov.margins || [];
    var hasOverride = !!(ovTiers.length || ovMargins.length);

    var overrideTierChecks = ['good', 'better', 'best'].map(function (t) {
      var checked = (ovTiers.indexOf(t) !== -1) ? ' checked' : '';
      return '<label><input type="checkbox" data-vg-pl-override-tier="' + escapeHtml(sub.key) + '" value="' + t + '"' + checked + '><span>' + TIER_LABELS[t] + '</span></label>';
    }).join('');
    var overrideMarginChecks = [1,2,3,4].map(function (m) {
      var checked = (ovMargins.indexOf(m) !== -1) ? ' checked' : '';
      return '<label><input type="checkbox" data-vg-pl-override-margin="' + escapeHtml(sub.key) + '" value="' + m + '"' + checked + '><span>' + MARGIN_LABELS[m] + '</span></label>';
    }).join('');

    var classes = 'vg-ple__item';
    if (isSelected) classes += ' vg-ple__item--checked';
    if (hasOverride) classes += ' vg-ple__item--expanded';

    return (
      '<div class="' + classes + '" data-vg-ple-item="' + escapeHtml(sub.key) + '">' +
        '<label class="vg-ple__checkbox">' +
          '<input type="checkbox" data-vg-pl-checkbox="' + escapeHtml(sub.key) + '"' + (isSelected ? ' checked' : '') + '>' +
          '<span class="vg-ple__name">' + escapeHtml(sub.name) + '</span>' +
        '</label>' +
        '<span class="vg-ple__badge" data-vg-pl-badge>custom</span>' +
        '<button type="button" class="vg-ple__override-btn" data-vg-pl-override-toggle="' + escapeHtml(sub.key) + '" aria-expanded="' + (hasOverride ? 'true' : 'false') + '">Customize</button>' +
        '<div class="vg-ple__override-panel" data-vg-pl-override-panel="' + escapeHtml(sub.key) + '">' +
          '<div class="vg-ple__override-row"><span class="vg-ple__override-label">Tier</span><div class="vg-placement-row__radios">' + overrideTierChecks + '</div></div>' +
          '<div class="vg-ple__override-row"><span class="vg-ple__override-label">Margin</span><div class="vg-placement-row__radios">' + overrideMarginChecks + '</div></div>' +
          '<button type="button" class="vg-ple__override-clear" data-vg-pl-override-clear="' + escapeHtml(sub.key) + '">Use vendor default</button>' +
        '</div>' +
      '</div>'
    );
  }

  function renderDocsEditor(d) {
    if (!d.documents || !d.documents.length) {
      return '<p class="vg-empty__text">No documents attached yet.</p>';
    }
    return '<div class="vg-doc-list">' +
      d.documents.map(function (doc) {
        return (
          '<div class="vg-doc-item">' +
            '<a class="vg-doc-item__name" href="' + escapeHtml(doc.url) + '" target="_blank" rel="noopener noreferrer">' +
              escapeHtml(doc.name) +
            '</a>' +
            (doc.type ? '<span class="vg-doc-item__type">' + escapeHtml(doc.type) + '</span>' : '') +
            '<button type="button" class="vg-doc-item__remove" data-vg-doc-remove="' + escapeHtml(doc.id) + '" aria-label="Remove">&times;</button>' +
          '</div>'
        );
      }).join('') +
    '</div>';
  }

  function renderDocAddForm() {
    return (
      '<div class="vg-doc-add">' +
        '<input class="vg-form__input" data-vg-doc-name type="text" placeholder="Document name (e.g., 2026 Dealer Pricing)" />' +
        '<input class="vg-form__input" data-vg-doc-type type="text" placeholder="Type (pricing, catalog, etc.)" />' +
        '<div class="vg-doc-add__url">' +
          '<input class="vg-form__input" data-vg-doc-url type="url" placeholder="Shopify Files URL — https://cdn.shopify.com/..." />' +
        '</div>' +
        '<div class="vg-doc-add__footer">' +
          '<span class="vg-doc-add__notice">Attaching a document emails all opted-in reps.</span>' +
          '<button type="button" class="vg-btn vg-btn--primary vg-btn--sm" data-vg-doc-attach>Attach document</button>' +
        '</div>' +
      '</div>'
    );
  }

  function renderFooter(mode) {
    if (mode === 'view') {
      if (isAdmin()) {
        return '<button type="button" class="vg-btn vg-btn--ghost" data-vg-modal-close>Close</button>' +
               '<button type="button" class="vg-btn vg-btn--primary" data-vg-modal-edit>Edit</button>';
      }
      return '<button type="button" class="vg-btn vg-btn--ghost" data-vg-modal-close>Close</button>';
    }
    // edit / new
    var deleteBtn = (mode === 'edit')
      ? '<button type="button" class="vg-btn vg-btn--danger-ghost" data-vg-vendor-delete>Delete vendor</button>'
      : '';
    return deleteBtn + // CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11): the Product Rules link left the footer (clutter); the PO Profiles table's Products column opens Product Rules
           '<span class="vg-modal__dirty" data-vg-dirty hidden>Unsaved changes</span>' + // CUSTOM_VGUX_BY_CLAUDE (#12)
           '<button type="button" class="vg-btn vg-btn--ghost" data-vg-modal-close>Cancel</button>' +
           '<button type="button" class="vg-btn vg-btn--primary" data-vg-vendor-save>Save vendor</button>';
  }

  // --- Draft mutators ---
  function collectDraftFromForm() {
    var d = state.modal.draft || blankVendorDraft();
    if (!d._ui) initPlacementsUi(d);
    qsa('[data-vg-field]', els.modalBody).forEach(function (el) {
      var field = el.getAttribute('data-vg-field');
      if (el.type === 'checkbox') d[field] = !!el.checked;
      else d[field] = el.value;
    });

    // Availability radio
    var availRadio = qs('[data-vg-availability]:checked', els.modalBody);
    d.availability = availRadio ? availRadio.value : '';

    // Brand-level tiers + margins (multi-select checkboxes)
    d.tiers = [];
    qsa('[data-vg-tier-checkbox]', els.modalBody).forEach(function (cb) {
      if (cb.checked) d.tiers.push(cb.getAttribute('data-vg-tier-checkbox'));
    });
    d.margins = [];
    qsa('[data-vg-margin-checkbox]', els.modalBody).forEach(function (cb) {
      if (cb.checked) d.margins.push(Number(cb.getAttribute('data-vg-margin-checkbox')));
    });

    // Derive placement defaults from brand-level selections
    d._ui.defaultTiers = d.tiers.length ? d.tiers : ['best'];
    d._ui.defaultMargins = d.margins.length ? d.margins : [3];

    // Checked sub-categories
    var newSelected = {};
    qsa('[data-vg-pl-checkbox]', els.modalBody).forEach(function (cb) {
      if (cb.checked) newSelected[cb.getAttribute('data-vg-pl-checkbox')] = true;
    });
    d._ui.selected = newSelected;

    // Per-row overrides (now multi-select checkboxes)
    var newOverrides = {};
    Object.keys(newSelected).forEach(function (subKey) {
      var ovTs = qsa('[data-vg-pl-override-tier="' + subKey + '"]:checked', els.modalBody).map(function (el) { return el.value; });
      var ovMs = qsa('[data-vg-pl-override-margin="' + subKey + '"]:checked', els.modalBody).map(function (el) { return Number(el.value); });
      if (ovTs.length || ovMs.length) {
        newOverrides[subKey] = { tiers: ovTs, margins: ovMs };
      }
    });
    d._ui.overrides = newOverrides;

    // Resolve into concrete placements payload
    d.placements = Object.keys(newSelected).map(function (subKey) {
      var ov = newOverrides[subKey] || {};
      return {
        subcategoryKey: subKey,
        tiers: ov.tiers && ov.tiers.length ? ov.tiers : d._ui.defaultTiers,
        margins: ov.margins && ov.margins.length ? ov.margins : d._ui.defaultMargins
      };
    });

    // PO Profile fields (admin only — section may not exist for non-admins)
    if (isAdmin()) {
      // Make sure all instruction list editors flush to their hidden inputs before
      // collectPoFromForm reads them.
      syncAllVendorPoInstrHidden();
      collectPoFromForm(d, els.modalBody);
    }

    state.modal.draft = d;
    return d;
  }

  // --- API actions ---
  async function saveVendor() {
    var draft = collectDraftFromForm();
    if (!draft.name || !draft.name.trim()) {
      showToast('Vendor name is required.', 'error');
      return;
    }
    // Clean placements — drop empties
    var placements = (draft.placements || []).filter(function (p) {
      return p.subcategoryKey && ((p.tiers && p.tiers.length) || (p.tier));
    });

    var payload = {
      name: draft.name.trim(),
      logoUrl: draft.logoUrl || '',
      tiers: draft.tiers || [],
      margins: draft.margins || [],
      leadTime: draft.leadTime || '',
      websiteUrl: draft.websiteUrl || '',
      categoryPageUrl: draft.categoryPageUrl || '',
      notes: draft.notes || '',
      availability: draft.availability || '',
      confidential: !!draft.confidential,
      defunct: !!draft.defunct,
      madeByHand: !!draft.madeByHand, // CUSTOM_BUILTTOORDER_BY_CLAUDE
      usuallyInStock: !!draft.usuallyInStock, // CUSTOM_USUALLYINSTOCK_BY_CLAUDE
      placements: placements
    };
    // Include PO Profile when admin has supplied it. Worker normalizes; empty po block is fine.
    if (isAdmin() && draft.po) payload.po = draft.po;

    var saveBtn = qs('[data-vg-vendor-save]', els.modalFooter);
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

    try {
      var resp;
      if (state.modal.mode === 'new') {
        resp = await apiFetch('/vendor', { method: 'POST', body: JSON.stringify(payload) });
      } else {
        /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Ryan 2026-10-04) - TWO DOORS, ONE RECORD. Product Rules writes
           this same record; naming the revision this modal opened on turns a clash into a 409 the
           person can read ("changed since you opened it") instead of a silent overwrite. */
        payload.basedOn = String((state.modal.draft && state.modal.draft.updatedAt) || 0);
        resp = await apiFetch('/vendor/' + encodeURIComponent(state.modal.vendorId), {
          method: 'PUT', body: JSON.stringify(payload)
        });
      }
      var saved = resp && resp.vendor;
      if (!saved) throw new Error('Save returned no vendor record');
      upsertVendor(saved);

      // If we just created a new vendor, flip to edit mode so docs can be added
      if (state.modal.mode === 'new') {
        state.modal.mode = 'edit';
        state.modal.vendorId = saved.id;
        state.modal.draft = JSON.parse(JSON.stringify(saved));
        initPlacementsUi(state.modal.draft);
        renderModal();
      } else {
        /* CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11, UX batch #12): Save keeps the pop-up open on the same tab and
           scroll position, now holding the saved record (so the next Save pins to the new revision). */
        var keepScroll = els.modalBody ? els.modalBody.scrollTop : 0;
        state.modal.draft = JSON.parse(JSON.stringify(saved));
        if (!state.modal.draft.placements) state.modal.draft.placements = [];
        if (!state.modal.draft.documents) state.modal.draft.documents = [];
        initPlacementsUi(state.modal.draft);
        renderModal();
        if (els.modalBody) els.modalBody.scrollTop = keepScroll;
        var savedBtn = qs('[data-vg-vendor-save]', els.modalFooter);
        if (savedBtn) {
          savedBtn.textContent = 'Saved ✓';
          setTimeout(function () { var b = qs('[data-vg-vendor-save]', els.modalFooter); if (b && b.textContent === 'Saved ✓') b.textContent = 'Save vendor'; }, 2000);
        }
      }

      rerenderAll();
      if (state.poStatus) { state.poStatus = null; if (state.view === 'po-profiles') renderPoProfilesView(); } // #13: recount
      showToast('Vendor saved', 'success');
    } catch (err) {
      /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE (Ryan 2026-08-27) - a 409 is the Alias Keywords uniqueness
         refusal, and it is the only save error here that the admin has to READ and act on rather
         than retry: it names the keyword and the vendor that already owns it. The default 5.5s
         error life is tuned for "Save failed: network error", which needs no reading. */
      showToast('Save failed: ' + errMsg(err), 'error', (err && err.status === 409) ? 12000 : undefined);
      if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save vendor'; }
    }
  }

  function requestDeleteVendor() {
    var id = state.modal.vendorId;
    var v = vendorById(id);
    if (!v) return;
    openConfirm('Delete "' + v.name + '"? This cannot be undone.', function () {
      deleteVendor(id);
    });
  }

  async function deleteVendor(id) {
    try {
      await apiFetch('/vendor/' + encodeURIComponent(id), { method: 'DELETE' });
      removeVendorById(id);
      closeVendorModal();
      rerenderAll();
      showToast('Vendor deleted', 'success');
    } catch (err) {
      showToast('Delete failed: ' + errMsg(err), 'error');
    }
  }

  async function attachDocument() {
    var id = state.modal.vendorId;
    if (!id) return;
    var nameEl = qs('[data-vg-doc-name]', els.modalBody);
    var typeEl = qs('[data-vg-doc-type]', els.modalBody);
    var urlEl = qs('[data-vg-doc-url]', els.modalBody);
    var name = nameEl ? nameEl.value.trim() : '';
    var type = typeEl ? typeEl.value.trim() : '';
    var url = urlEl ? urlEl.value.trim() : '';
    if (!name || !type || !url) {
      showToast('Document name, type, and URL are all required.', 'error');
      return;
    }
    var attachBtn = qs('[data-vg-doc-attach]', els.modalBody);
    if (attachBtn) { attachBtn.disabled = true; attachBtn.textContent = 'Attaching…'; }
    try {
      await apiFetch('/vendor/' + encodeURIComponent(id) + '/document', {
        method: 'POST',
        body: JSON.stringify({ name: name, type: type, url: url })
      });
      // Refresh the vendor from the server to get the new doc
      var refreshed = await apiFetch('/vendor/' + encodeURIComponent(id));
      if (refreshed && refreshed.vendor) {
        upsertVendor(refreshed.vendor);
        // Preserve any in-progress edits to other fields — only sync docs.
        if (state.modal.draft) {
          state.modal.draft.documents = refreshed.vendor.documents || [];
        }
        refreshDocsSection();
      }
      var nameElReset = qs('[data-vg-doc-name]', els.modalBody);
      var typeElReset = qs('[data-vg-doc-type]', els.modalBody);
      var urlElReset = qs('[data-vg-doc-url]', els.modalBody);
      if (nameElReset) nameElReset.value = '';
      if (typeElReset) typeElReset.value = '';
      if (urlElReset) urlElReset.value = '';
      showToast('Document attached — recipients notified via next digest', 'success');
    } catch (err) {
      showToast('Attach failed: ' + errMsg(err), 'error');
    } finally {
      if (attachBtn) { attachBtn.disabled = false; attachBtn.textContent = 'Attach document'; }
    }
  }

  function requestDeleteDocument(docId) {
    var vendorId = state.modal.vendorId;
    if (!vendorId || !docId) return;
    openConfirm('Remove this document?', function () {
      deleteDocument(vendorId, docId);
    });
  }

  async function deleteDocument(vendorId, docId) {
    try {
      await apiFetch('/vendor/' + encodeURIComponent(vendorId) + '/document/' + encodeURIComponent(docId), {
        method: 'DELETE'
      });
      var refreshed = await apiFetch('/vendor/' + encodeURIComponent(vendorId));
      if (refreshed && refreshed.vendor) {
        upsertVendor(refreshed.vendor);
        // Preserve any in-progress edits to other fields — only sync docs.
        if (state.modal.draft) {
          state.modal.draft.documents = refreshed.vendor.documents || [];
        }
        refreshDocsSection();
      }
      showToast('Document removed', 'success');
    } catch (err) {
      showToast('Delete document failed: ' + errMsg(err), 'error');
    }
  }

  function refreshDocsSection() {
    var host = qs('[data-vg-docs]', els.modalBody);
    if (host) host.innerHTML = renderDocsEditor(state.modal.draft);
    // Clear add form inputs
    ['[data-vg-doc-name]', '[data-vg-doc-type]', '[data-vg-doc-url]'].forEach(function (sel) {
      var el = qs(sel, els.modalBody);
      if (el) el.value = '';
    });
  }

  function rerenderAll() {
    // Category view side
    renderCategoryTiles();
    if (state.selectedCategoryKey) renderSubcategoryList(state.selectedCategoryKey);
    if (state.selectedSubcategoryKey) renderRankedVendors(state.selectedSubcategoryKey);
    // Vendor view side
    if (state.view === 'vendor') renderVendorView();
  }

  // --- Confirm dialog ---
  function openConfirm(message, onConfirm) {
    if (!els.confirm) return;
    state.confirm.open = true;
    state.confirm.onConfirm = onConfirm;
    els.confirmMessage.textContent = message;
    els.confirm.hidden = false;
  }

  function closeConfirm() {
    state.confirm.open = false;
    state.confirm.onConfirm = null;
    if (els.confirm) els.confirm.hidden = true;
  }

  // ------------------------------------------------------------------
  // Event wiring
  // ------------------------------------------------------------------
  function wireEvents() {
    // View toggle
    els.viewToggle.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-view]');
      if (!btn) return;
      setView(btn.getAttribute('data-view'));
    });

    // Category tile click
    els.catGrid.addEventListener('click', function (e) {
      var tile = e.target.closest('[data-cat-key]');
      if (!tile) return;
      enterCategory(tile.getAttribute('data-cat-key'));
    });

    // Sub-category click
    els.subcatList.addEventListener('click', function (e) {
      var item = e.target.closest('[data-sub-key]');
      if (!item) return;
      enterSubcategory(item.getAttribute('data-sub-key'));
    });

    // Back to top-level categories (used in subcat + ranked breadcrumbs)
    qsa('[data-vg-back-to-categories]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.selectedCategoryKey = null;
        state.selectedSubcategoryKey = null;
        showCategoryLevel('grid');
      });
    });

    // Back to sub-category list from ranked view
    els.backToSubcat.addEventListener('click', function () {
      var catKey = els.backToSubcat.getAttribute('data-cat-key');
      if (catKey) enterCategory(catKey);
    });

    // Retry
    els.retry.addEventListener('click', function () { loadAll(); });

    // --- Filter bar ---
    if (els.filters) {
      // Filter button toggles (open/close dropdown)
      els.filters.addEventListener('click', function (e) {
        var toggle = e.target.closest('[data-vg-filter-toggle]');
        if (toggle) {
          e.stopPropagation();
          toggleFilterDropdown(toggle.getAttribute('data-vg-filter-toggle'));
          return;
        }
      });

      // Checkbox changes
      els.filters.addEventListener('change', function (e) {
        if (e.target.matches('[data-vg-filter]')) applyFilters();
      });

      // Search input
      if (els.search) {
        var searchTimer = null;
        els.search.addEventListener('input', function () {
          clearTimeout(searchTimer);
          searchTimer = setTimeout(applyFilters, 120);
        });
      }

      // Clear all (inside + outside buttons on mobile)
      qsa('[data-vg-filters-clear]').forEach(function (btn) {
        btn.addEventListener('click', clearFilters);
      });
    }

    // Close dropdowns on outside click
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.vg-filter')) closeAllDropdowns();
    });

    // --- Group-by toggle ---
    var groupWrap = qs('[data-vg-group-by-wrap]');
    if (groupWrap) {
      groupWrap.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-vg-group-by]');
        if (!btn) return;
        state.groupBy = btn.getAttribute('data-vg-group-by');
        renderVendorView();
      });
    }

    // --- Admin toggle ---
    if (els.adminToggle) {
      els.adminToggle.addEventListener('click', toggleAdminMode);
    }
    if (els.adminBar) {
      els.adminBar.addEventListener('click', function (e) {
        if (e.target.closest('[data-vg-admin-add]')) {
          openVendorModal(null, 'new');
          return;
        }
        // CUSTOM_GUIDEWRITE_BY_CLAUDE (AIOPS 78278a7aa193) — show what an agent changed.
        var logBtn = e.target.closest('[data-vg-writelog-toggle]');
        if (logBtn) toggleWriteLog(logBtn);
        var rcBtn = e.target.closest('[data-vg-recipients-toggle]'); // CUSTOM_VGUX_BY_CLAUDE
        if (rcBtn) toggleRecipientsPanel(rcBtn);
      });
    }

    // --- Vendor card click (delegated — works across all render targets) ---
    /* CUSTOM_PALLETEXCLUDE_BY_CLAUDE (Ryan 2026-09-19) - the exclusion picker's search box.
       Delegated, because the vendor modal is re-rendered wholesale and a listener bound to the
       input itself would be thrown away with it. */
    qs('.vg-wrap').addEventListener('input', function (e) {
      var pfxSearchEl = e.target.closest('[data-vg-pfx-search]');
      if (!pfxSearchEl) return;
      pfxRenderList(pfxSearchEl.closest('[data-vg-pfx]'));
    });

    qs('.vg-wrap').addEventListener('click', function (e) {
      // CUSTOM_VGUX_BY_CLAUDE (#13 / #15): the Ordering ready cell lists what is missing; the Products count opens
      // Product Rules on that supplier. Both sit inside the row button, so they answer first.
      var readyCell = e.target.closest('[data-vg-po-ready]');
      if (readyCell) {
        e.stopPropagation(); e.preventDefault();
        var rid = readyCell.getAttribute('data-vg-po-ready');
        state.poMissingOpen = state.poMissingOpen === rid ? null : rid;
        renderPoProfilesView();
        return;
      }
      var prodCell = e.target.closest('[data-vg-po-products]');
      if (prodCell) {
        e.stopPropagation(); e.preventDefault();
        window.open('/pages/rep-product-rules#g=' + encodeURIComponent(prodCell.getAttribute('data-vg-po-products')), '_blank', 'noopener');
        return;
      }
      // PO Profiles tab — row click (admin only)
      var poRow = e.target.closest('[data-vg-po-edit]');
      if (poRow) {
        e.stopPropagation();
        openVendorModal(poRow.getAttribute('data-vg-po-edit'), 'edit');
        // Scroll the modal body to the PO section after it renders
        setTimeout(function () {
          var sec = els.modalBody && els.modalBody.querySelector('.vg-modal__section--po');
          if (sec && sec.scrollIntoView) sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
        return;
      }
      // PO Profiles tab — migrate seed addresses
      var migBtn = e.target.closest('[data-vg-po-migrate]');
      if (migBtn && isAdmin()) {
        e.stopPropagation();
        runPoMigration(migBtn);
        return;
      }
      // PO Profile section — "Copy this PO data to other vendors..." controls
      var copyToggle = e.target.closest('[data-vg-po-copy-toggle]');
      if (copyToggle && isAdmin()) {
        e.stopPropagation();
        var panel = els.modalBody.querySelector('[data-vg-po-copy-panel]');
        if (panel) {
          var open = !panel.hidden;
          panel.hidden = open;
          copyToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        }
        return;
      }
      var copyCancel = e.target.closest('[data-vg-po-copy-cancel]');
      if (copyCancel && isAdmin()) {
        e.stopPropagation();
        var panelC = els.modalBody.querySelector('[data-vg-po-copy-panel]');
        var togC = els.modalBody.querySelector('[data-vg-po-copy-toggle]');
        if (panelC) panelC.hidden = true;
        if (togC) togC.setAttribute('aria-expanded', 'false');
        // Uncheck all targets so reopening starts fresh
        qsa('[data-vg-po-copy-target]', els.modalBody).forEach(function (cb) { cb.checked = false; });
        return;
      }
      var copyApply = e.target.closest('[data-vg-po-copy-apply]');
      if (copyApply && isAdmin()) {
        e.stopPropagation();
        copyPoToOtherVendors(copyApply);
        return;
      }
      /* CUSTOM_PALLETEXCLUDE_BY_CLAUDE (Ryan 2026-09-19) - pallet-fee exclusion picker.
         Open/close lazily loads this vendor's products (one KV-backed call, cached per vendor). */
      var pfxToggle = e.target.closest('[data-vg-pfx-toggle]');
      if (pfxToggle && isAdmin()) {
        e.stopPropagation();
        var pfxWrap = pfxToggle.closest('[data-vg-pfx]');
        var pfxPanel = pfxWrap && pfxWrap.querySelector('[data-vg-pfx-panel]');
        if (pfxPanel) {
          var pfxWasOpen = !pfxPanel.hidden;
          pfxPanel.hidden = pfxWasOpen;
          pfxToggle.setAttribute('aria-expanded', pfxWasOpen ? 'false' : 'true');
          if (!pfxWasOpen) {
            pfxLoad(pfxWrap);
            pfxRenderList(pfxWrap);
            var pfxSearch = pfxWrap.querySelector('[data-vg-pfx-search]');
            if (pfxSearch) pfxSearch.focus();
          }
        }
        return;
      }
      var pfxItem = e.target.closest('[data-vg-pfx-item]');
      if (pfxItem && isAdmin()) {
        e.stopPropagation();
        var pfxItemWrap = pfxItem.closest('[data-vg-pfx]');
        if (pfxItemWrap) {
          var pfxH = (pfxItem.getAttribute('data-vg-pfx-item') || '').trim().toLowerCase();
          var pfxCur = pfxGet(pfxItemWrap);
          var pfxIdx = pfxCur.indexOf(pfxH);
          if (pfxIdx >= 0) pfxCur.splice(pfxIdx, 1); else pfxCur.push(pfxH);
          pfxSet(pfxItemWrap, pfxCur);
          /* Repainted IN PLACE, not re-rendered. A list re-render jumps the scroll back to the top
             on every click, and the whole interaction is clicking several rows in a row. */
          pfxItem.classList.toggle('is-off', pfxIdx < 0);
          var pfxMark = pfxItem.querySelector('.vg-pfx__mark');
          if (pfxMark) pfxMark.innerHTML = (pfxIdx < 0) ? '&times;' : '';
        }
        return;
      }
      /* END CUSTOM_PALLETEXCLUDE_BY_CLAUDE */
      // Vendor PO Profile — Default Instructions list editor (X to remove, Add to append)
      var instrAddBtn = e.target.closest('[data-vg-po-instr-add]');
      if (instrAddBtn && isAdmin()) {
        e.stopPropagation();
        addVendorPoInstructionTo(instrAddBtn.closest('[data-vg-po-instr-editor]'));
        return;
      }
      var instrRemoveBtn = e.target.closest('[data-vg-po-instr-remove]');
      if (instrRemoveBtn && isAdmin()) {
        e.stopPropagation();
        var editor = instrRemoveBtn.closest('[data-vg-po-instr-editor]');
        var li = instrRemoveBtn.closest('.vg-po-instr-item');
        if (li) {
          li.remove();
          syncVendorPoInstrHidden(editor);
        }
        return;
      }
      // Explicit edit pencil
      var editBtn = e.target.closest('[data-vg-edit-vendor]');
      if (editBtn) {
        e.stopPropagation();
        openVendorModal(editBtn.getAttribute('data-vg-edit-vendor'), 'edit');
        return;
      }
      // Whole card click — view mode (or edit if admin mode is on)
      var card = e.target.closest('.vg-card');
      if (card && !e.target.closest('.vg-modal') && !e.target.closest('.vg-confirm')) {
        var id = card.getAttribute('data-vendor-id');
        if (id) {
          var mode = (state.adminMode && isAdmin()) ? 'edit' : 'view';
          openVendorModal(id, mode);
        }
      }
    });

    // Keyboard activation on cards (role="button")
    qs('.vg-wrap').addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest('.vg-card');
      if (card) {
        e.preventDefault();
        var id = card.getAttribute('data-vendor-id');
        if (id) {
          var mode = (state.adminMode && isAdmin()) ? 'edit' : 'view';
          openVendorModal(id, mode);
        }
      }
    });

    // --- Modal ---
    if (els.modal) {
      // Close (X, backdrop, Cancel)
      els.modal.addEventListener('click', function (e) {
        // Admin help toggle
        if (e.target.closest('[data-vg-modal-help]')) {
          toggleModalHelp();
          return;
        }
        // Click outside the help popover closes it (but don't close the modal)
        if (els.modalHelpPop && !els.modalHelpPop.hidden && !e.target.closest('[data-vg-modal-help-pop]')) {
          closeModalHelp();
        }
        if (e.target.closest('[data-vg-modal-close]')) {
          requestCloseVendorModal(); // CUSTOM_VGUX_BY_CLAUDE (#12): asks first when there are unsaved changes
          return;
        }
        // CUSTOM_VGUX_BY_CLAUDE (#12): adding or removing an instruction line is an edit
        if (e.target.closest('[data-vg-po-instr-add], [data-vg-po-instr-remove], [data-vg-pfx-item], [data-vg-pl-override-clear]')) markModalDirty(e);
        // View → Edit swap
        if (e.target.closest('[data-vg-modal-edit]')) {
          state.modal.mode = 'edit';
          renderModal();
          return;
        }
        /* CUSTOM_VENDOR_GUIDE_BY_CLAUDE — Tab switch (Vendor Info ↔ PO Profile).
           Toggle DOM visibility in place instead of re-rendering so any
           in-progress edits on the hidden tab are preserved. state.modal
           .activeTab is mirrored so renderModal() reopens to the right tab
           if anything else triggers a full re-render (e.g. recipients load). */
        var tabBtn = e.target.closest('[data-vg-modal-tab]');
        if (tabBtn) {
          var target = tabBtn.getAttribute('data-vg-modal-tab');
          state.modal.activeTab = target;
          var allTabs = qsa('[data-vg-modal-tab]', els.modalBody);
          allTabs.forEach(function (t) {
            var isActive = t.getAttribute('data-vg-modal-tab') === target;
            t.classList.toggle('vg-modal__tab--active', isActive);
            t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });
          var allPanels = qsa('[data-vg-modal-tabpanel]', els.modalBody);
          allPanels.forEach(function (p) {
            p.hidden = p.getAttribute('data-vg-modal-tabpanel') !== target;
          });
          // Scroll to top of the new tab so admin starts at the section header.
          if (els.modalBody) els.modalBody.scrollTop = 0;
          return;
        }
        /* END CUSTOM_VENDOR_GUIDE_BY_CLAUDE */
        // Save
        if (e.target.closest('[data-vg-vendor-save]')) {
          saveVendor();
          return;
        }
        // Delete vendor
        if (e.target.closest('[data-vg-vendor-delete]')) {
          requestDeleteVendor();
          return;
        }
        // Placement override toggle (expand/collapse per-row custom panel)
        var ovToggle = e.target.closest('[data-vg-pl-override-toggle]');
        if (ovToggle) {
          var item = ovToggle.closest('[data-vg-ple-item]');
          if (item) {
            var willExpand = !item.classList.contains('vg-ple__item--expanded');
            item.classList.toggle('vg-ple__item--expanded', willExpand);
            ovToggle.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
          }
          return;
        }
        // Placement override clear (revert row to vendor default)
        var ovClear = e.target.closest('[data-vg-pl-override-clear]');
        if (ovClear) {
          var subKey = ovClear.getAttribute('data-vg-pl-override-clear');
          qsa('[data-vg-pl-override-tier="' + subKey + '"], [data-vg-pl-override-margin="' + subKey + '"]', els.modalBody)
            .forEach(function (r) { r.checked = false; });
          var clearItem = ovClear.closest('[data-vg-ple-item]');
          if (clearItem) clearItem.classList.remove('vg-ple__item--expanded');
          return;
        }
        // Clicks inside override panels — stop bubbling
        if (e.target.closest('[data-vg-pl-override-panel]')) {
          e.stopPropagation();
          return;
        }
        // Doc attach
        if (e.target.closest('[data-vg-doc-attach]')) {
          attachDocument();
          return;
        }
        // Doc remove
        var docRemove = e.target.closest('[data-vg-doc-remove]');
        if (docRemove) {
          requestDeleteDocument(docRemove.getAttribute('data-vg-doc-remove'));
          return;
        }
      });

      // CUSTOM_VGUX_BY_CLAUDE (Ryan 2026-10-11): the "Upload emails" panel's switches and sample button.
      if (els.recipientsPanel) {
        els.recipientsPanel.addEventListener('change', function (e) {
          var t = e.target.closest('[data-vg-recipient-toggle]');
          if (t) toggleRecipientOptIn(t.getAttribute('data-code'), t.checked, t);
        });
        els.recipientsPanel.addEventListener('click', function (e) {
          if (e.target.closest('[data-vg-recipient-test]')) sendTestNotification();
        });
      }

      // CUSTOM_VGUX_BY_CLAUDE (#12): typing in, or changing, any field of the record marks the pop-up unsaved
      els.modal.addEventListener('input', markModalDirty);
      els.modal.addEventListener('change', markModalDirty);
      window.addEventListener('beforeunload', function (e) {
        if (state.modal.open && state.modal.dirty) { e.preventDefault(); e.returnValue = ''; }
      });

      // Enter key in any PO Default Instructions input → add the line to its editor
      els.modal.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        var input = e.target.closest('[data-vg-po-instr-input]');
        if (input) {
          e.preventDefault();
          addVendorPoInstructionTo(input.closest('[data-vg-po-instr-editor]'));
        }
      });

      // Placement sub-category checkboxes — toggle row class without re-render
      els.modal.addEventListener('change', function (e) {
        // Category "select all" checkbox — cascades to all sub-category checkboxes
        var selectAll = e.target.closest('[data-vg-pl-select-all]');
        if (selectAll) {
          var catEl = selectAll.closest('.vg-ple__cat');
          if (catEl) {
            var willCheck = selectAll.checked;
            var subs = qsa('[data-vg-pl-checkbox]', catEl);
            subs.forEach(function (sub) {
              sub.checked = willCheck;
              var item = sub.closest('[data-vg-ple-item]');
              if (item) item.classList.toggle('vg-ple__item--checked', willCheck);
            });
            var badge = qs('[data-vg-ple-count]', catEl);
            if (badge) {
              var count = willCheck ? subs.length : 0;
              badge.textContent = count + ' / ' + subs.length;
              badge.classList.toggle('vg-ple__cat-count--active', count > 0);
            }
          }
          return;
        }

        var cb = e.target.closest('[data-vg-pl-checkbox]');
        if (cb) {
          var item = cb.closest('[data-vg-ple-item]');
          if (item) item.classList.toggle('vg-ple__item--checked', !!cb.checked);
          var catEl = cb.closest('.vg-ple__cat');
          if (catEl) {
            var all = qsa('[data-vg-pl-checkbox]', catEl);
            var checked = all.filter(function (n) { return n.checked; }).length;
            var badge = qs('[data-vg-ple-count]', catEl);
            if (badge) {
              badge.textContent = checked + ' / ' + all.length;
              badge.classList.toggle('vg-ple__cat-count--active', checked > 0);
            }
            // Sync "select all" checkbox state
            var selAllCb = qs('[data-vg-pl-select-all]', catEl);
            if (selAllCb) selAllCb.checked = (checked === all.length);
          }
          return;
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Toggle the flat-rate cost input row
           when the Shipping Model dropdown changes. */
        /* CUSTOM_FREIGHTMODE_BY_CLAUDE - the locked-rate fields show only under Locked rate. */
        if (e.target && (e.target.id === 'vg-po-freightMode' || e.target.id === 'vg-po-shippingModel' || e.target.id === 'vg-po-flatRateBasis')) {
          syncFreightRows(); // CUSTOM_PERPRODUCTFREIGHT_BY_CLAUDE
        }
        /* Gray out the freight-request email block unless workflow is Freight → PO. */
        if (e.target && e.target.id === 'vg-po-workflow') {
          var frBlock = els.modalBody && els.modalBody.querySelector('[data-vg-po-freight-block]');
          if (frBlock) {
            var freightOn = e.target.value === 'freight-then-po';
            frBlock.disabled = !freightOn;
            var naBadge = frBlock.querySelector('[data-vg-po-freight-na]');
            if (naBadge) naBadge.style.display = freightOn ? 'none' : '';
          }
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      });
    }

    // --- Confirm dialog ---
    if (els.confirm) {
      els.confirm.addEventListener('click', function (e) {
        if (e.target.closest('[data-vg-confirm-cancel]')) {
          closeConfirm();
          return;
        }
        if (e.target.closest('[data-vg-confirm-ok]')) {
          var fn = state.confirm.onConfirm;
          closeConfirm();
          if (typeof fn === 'function') fn();
        }
      });
    }

    // --- Escape key closes modal / confirm ---
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (state.confirm.open) { closeConfirm(); return; }
      if (els.modalHelpPop && !els.modalHelpPop.hidden) { closeModalHelp(); return; }
      if (state.modal.open) { requestCloseVendorModal(); } // CUSTOM_VGUX_BY_CLAUDE (#12)
    });
  }

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  function cacheEls() {
    els.viewToggle   = qs('.vg-view-toggle');
    els.catGrid      = qs('[data-vg-cat-grid]');
    els.subcat       = qs('[data-vg-subcat]');
    els.subcatList   = qs('[data-vg-subcat-list]');
    els.subcatTitle  = qs('[data-vg-subcat-title]');
    els.ranked       = qs('[data-vg-ranked]');
    els.tiers        = qs('[data-vg-tiers]');
    els.rankedTitle  = qs('[data-vg-ranked-title]');
    els.backToSubcat = qs('[data-vg-back-to-subcat]');
    els.az           = qs('[data-vg-az]');
    els.vendorGrid   = qs('[data-vg-vendor-grid]');
    els.loading      = qs('[data-vg-loading]');
    els.error        = qs('[data-vg-error]');
    els.errorDetail  = qs('[data-vg-error-detail]');
    els.retry        = qs('[data-vg-retry]');
    els.filters      = qs('[data-vg-filters]');
    els.search       = qs('[data-vg-search]');
    // Admin surface
    els.adminStrip   = qs('[data-vg-admin-strip]');
    els.adminToggle  = qs('[data-vg-admin-toggle]');
    els.adminBanner  = qs('[data-vg-admin-banner]');
    els.adminBar     = qs('[data-vg-admin-bar]');
    // CUSTOM_GUIDEWRITE_BY_CLAUDE (AIOPS 78278a7aa193) — the agent-edit log panel.
    els.writeLog     = qs('[data-vg-writelog]');
    els.writeLogBody = qs('[data-vg-writelog-body]');
    els.recipientsPanel = qs('[data-vg-recipients-panel]'); // CUSTOM_VGUX_BY_CLAUDE: "Upload emails"
    els.recipientsBody  = qs('[data-vg-recipients-body]');
    // Modal
    els.modal        = qs('[data-vg-modal]');
    els.modalTitle   = qs('[data-vg-modal-title]');
    els.modalBody    = qs('[data-vg-modal-body]');
    els.modalFooter  = qs('[data-vg-modal-footer]');
    els.modalHelp    = qs('[data-vg-modal-help]');
    els.modalHelpPop = qs('[data-vg-modal-help-pop]');
    // Confirm
    els.confirm         = qs('[data-vg-confirm]');
    els.confirmMessage  = qs('[data-vg-confirm-message]');
  }

  function init() {
    if (!qs('.vg-wrap')) return;
    var session = requireSession();
    if (!session) return;
    state.session = session;
    cacheEls();
    populateHeader();
    initAdminSurface();
    wireEvents();
    loadAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
